import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Button,
  Typography,
  TextField,
  Stack,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Icon } from '@iconify/react';
import Popover from '@mui/material/Popover';
import DialogActions from '@mui/material/DialogActions';
import Services from '../../services/Services';
import { tokenService } from 'src/services';
import CountryAutocomplete from 'src/components/shared/CountryAutocomplete';
import CountryList, { getDialCode } from 'src/data/CountryList';
import PostalCodeAutocomplete from 'src/components/shared/PostalCodeAutocomplete';
import CityAutocomplete from 'src/components/shared/CityAutocomplete';
import { isNoPostalCodeCountry } from 'src/data/DHLNoPostalCodeCountries';
import DialCodeSelect from 'src/components/shared/DialCodeSelect';
import DHLPackageTypes, { findPackageType, getPackageTypeLabel } from 'src/data/DHLPackageTypes';
import { formatPhoneDisplay, formatPhoneInput, limitPhoneInput, parsePhone, toNationalPhoneDigits } from 'src/utils/phoneFormat';

// Boş/hatalı girişte NaN yerine 0 döner; NaN JSON'a null olarak gidiyordu.
const toNumber = (value) => {
  const parsed = parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
};

const CURRENCY_SYMBOLS = { EUR: '€', TRY: '₺', USD: '$', GBP: '£' };

// Tabloda tutarların para birimi belli olsun diye sembolle yazılır: "86.06 €"
const formatMoney = (value, currencyCode) =>
  `${Number(value || 0).toFixed(2)} ${CURRENCY_SYMBOLS[currencyCode] || currencyCode}`;

// TL karşılığı kuruşsuz ve daima yukarı yuvarlanır: 4850.83 -> "4851 ₺"
const formatTry = (value) => `${Math.ceil(Number(value || 0))} ${CURRENCY_SYMBOLS.TRY}`;

// DHL'in "Type Of Export" listesi, aynı sırayla. value alanları DHL API'sinin
// exportReason enum değerleridir.
const EXPORT_REASONS = [
  { value: 'PERMANENT', label: 'Kalıcı İhracat (PERMANENT)' },
  { value: 'RETURN', label: 'İade (RETURN)' },
  { value: 'SAMPLE', label: 'Numune (SAMPLE)' },
  { value: 'COMMERCIAL_PURPOSE_OR_SALE', label: 'Ticari Amaç veya Satış (COMMERCIAL PURPOSE OR SALE)' },
  { value: 'DIPLOMATIC_GOODS', label: 'Diplomatik Eşya (DIPLOMATIC GOODS)' },
  { value: 'RETURN_TO_ORIGIN', label: 'Çıkış Noktasına İade (RETURN TO ORIGIN)' },
  { value: 'PERSONAL_BELONGINGS_OR_PERSONAL_USE', label: 'Kişisel Eşya veya Kişisel Kullanım (PERSONAL BELONGINGS OR PERSONAL USE)' },
  { value: 'GIFT', label: 'Hediye (GIFT)' },
];

// DHL'in "Shipment Description" listesindeki döküman tipleri, aynı sırayla.
// value alanı DHL'e gönderilen değerdir, İngilizce ve büyük harf kalmalı.
const DOCUMENT_TYPES = [
  { value: 'ADVERTISING BROCHURES', label: 'Reklam Broşürleri (ADVERTISING BROCHURES)' },
  { value: 'ANNUAL REPORTS(PRINTED MATTER)', label: 'Yıllık Raporlar (ANNUAL REPORTS(PRINTED MATTER))' },
  { value: 'BILL OF LADING', label: 'Konşimento (BILL OF LADING)' },
  { value: 'BLANK FORMS', label: 'Boş Formlar (BLANK FORMS)' },
  { value: 'BLUEPRINTS', label: 'Teknik Çizimler (BLUEPRINTS)' },
  { value: 'BOOKLETS, BROCHURES (NON-ADV.)', label: 'Kitapçıklar, Broşürler - Reklam Dışı (BOOKLETS, BROCHURES (NON-ADV.))' },
  { value: 'BOOKS (NON-COMMERCIAL USE)', label: 'Kitaplar - Ticari Olmayan (BOOKS (NON-COMMERCIAL USE))' },
  { value: 'CATALOGUES', label: 'Kataloglar (CATALOGUES)' },
  { value: 'CERTIFICATES', label: 'Sertifikalar (CERTIFICATES)' },
  { value: 'CHARTS', label: 'Çizelgeler (CHARTS)' },
  { value: 'CHEQUES, CASHIER (NI)', label: 'Çekler, Kasa Çeki (CHEQUES, CASHIER (NI))' },
  { value: 'COMPLETED FORMS', label: 'Doldurulmuş Formlar (COMPLETED FORMS)' },
  { value: 'CONTRACT', label: 'Sözleşme (CONTRACT)' },
  { value: 'CREDIT NOTE', label: 'Alacak Dekontu (CREDIT NOTE)' },
  { value: 'DIPLOMATIC MAIL', label: 'Diplomatik Posta (DIPLOMATIC MAIL)' },
  { value: 'DIPLOMATIC MATERIAL', label: 'Diplomatik Materyal (DIPLOMATIC MATERIAL)' },
  { value: 'DOCUMENTS, GENERAL BUSINESS', label: 'Dokümanlar, Genel Ticari (DOCUMENTS, GENERAL BUSINESS)' },
  { value: 'EDUCATIONAL MATERIAL (PRINTED)', label: 'Eğitim Materyali - Basılı (EDUCATIONAL MATERIAL (PRINTED))' },
  { value: 'EXAMINATION PAPERS', label: 'Sınav Evrakları (EXAMINATION PAPERS)' },
  { value: 'IDENTITY DOCUMENT', label: 'Kimlik Belgesi (IDENTITY DOCUMENT)' },
  { value: 'INVOICES, NOT BLANK', label: 'Faturalar, Boş Olmayan (INVOICES, NOT BLANK)' },
  { value: 'ISSUED AIRLINE TICKETS', label: 'Düzenlenmiş Uçak Biletleri (ISSUED AIRLINE TICKETS)' },
  { value: 'LABELS', label: 'Etiketler (LABELS)' },
  { value: 'LETTER OR CORRESPONDENCE', label: 'Mektup veya Yazışma (LETTER OR CORRESPONDENCE)' },
  { value: 'MAGAZINES, PERIODICALS, JOURNALS', label: 'Dergiler, Süreli Yayınlar (MAGAZINES, PERIODICALS, JOURNALS)' },
  { value: 'MANUALS, TECHNICAL', label: 'Teknik Kılavuzlar (MANUALS, TECHNICAL)' },
  { value: 'MANUSCRIPTS', label: 'El Yazmaları (MANUSCRIPTS)' },
  { value: 'MAPS', label: 'Haritalar (MAPS)' },
  { value: 'MEDICAL EXAMINATION RESULT', label: 'Tıbbi Muayene Sonucu (MEDICAL EXAMINATION RESULT)' },
  { value: 'PASSPORTS', label: 'Pasaportlar (PASSPORTS)' },
  { value: 'PHOTOGRAPHS', label: 'Fotoğraflar (PHOTOGRAPHS)' },
  { value: 'PHOTOS AS PART OF BUSINESS REPORTS', label: 'İş Raporlarındaki Fotoğraflar (PHOTOS AS PART OF BUSINESS REPORTS)' },
  { value: 'PRICE LISTS', label: 'Fiyat Listeleri (PRICE LISTS)' },
  { value: 'PRICE TICKETS FOR GARMENTS', label: 'Giysi Fiyat Etiketleri (PRICE TICKETS FOR GARMENTS)' },
  { value: 'PRINTED MATTER', label: 'Basılı Materyal (PRINTED MATTER)' },
  { value: 'PRINTED MUSIC OR MANUSCRIPT', label: 'Basılı Nota veya El Yazması (PRINTED MUSIC OR MANUSCRIPT)' },
  { value: 'SHIPPING SCHEDULES', label: 'Sevkiyat Programları (SHIPPING SCHEDULES)' },
  { value: 'TECHNICAL PLANS AND DRAWINGS', label: 'Teknik Planlar ve Çizimler (TECHNICAL PLANS AND DRAWINGS)' },
  { value: 'VISA APPLICATIONS', label: 'Vize Başvuruları (VISA APPLICATIONS)' },
];

// DHL gümrük kalemi "Paket Türü" seçenekleri
const CUSTOMS_PACKAGE_TYPES = ['Pieces', 'Set', 'Boxes', 'Dozen', 'Pairs', 'Centimeters'];

// Adım başlıklarını dinamik olarak oluştur
function getSteps(docOrPackage) {
  const baseSteps = ['Kimden', 'Kime', 'Gönderi Türü', 'Gönderi İçeriği'];
  if (docOrPackage === 'Paket') {
    return [...baseSteps, 'Gümrük Bilgileri'];
  }
  return baseSteps;
}

// DHL yanıtında deliveryTypeCode her üründe "QDDF" geliyor; ayırt edici bilgi
// teslimat saati. 23:59 gün sonu demek, diğerleri "saatten önce" olarak yazılır.
// Ek, saatin okunuşuna göre değişiyor (9'dan, 12'den, 3'ten).
const HOUR_SUFFIXES = [
  'dan', 'den', 'den', 'ten', 'ten', 'ten', 'dan', 'den', 'den', 'dan',
  'dan', 'den', 'den', 'ten', 'ten', 'ten', 'dan', 'den', 'den', 'dan',
  'den', 'den', 'den', 'ten',
];

function getDeliveryTimeText(estimatedDeliveryDateAndTime) {
  if (!estimatedDeliveryDateAndTime) return '';
  const time = String(estimatedDeliveryDateAndTime).slice(11, 16);
  if (!time) return '';
  const [hour, minute] = time.split(':');
  if (time === '23:59' || time === '00:00') {
    return 'Gün Sonundan önce';
  }
  const suffix = HOUR_SUFFIXES[Number(hour)] || 'dan';
  return `${hour}:${minute}'${suffix} önce`;
}

// Özet ekranlarında ülke kodunu okunabilir ada çevirir (seçim alanlarıyla
// aynı biçim: İngilizce ad + kod).
function getCountryLabel(code) {
  if (!code) return '';
  const match = CountryList.find((country) => country.code === code);
  return match ? `${match.name} (${match.code})` : code;
}

// Özet ekranında DHL ülkeyi İngilizce ve büyük harfle yazıyor (GERMANY, TURKEY).
function getCountryNameUpper(code) {
  if (!code) return '';
  const match = CountryList.find((country) => country.code === code);
  return (match?.name || code).toLocaleUpperCase('tr-TR');
}

// --- Alan doğrulayıcıları ---------------------------------------------------

const onlyDigits = (value) => String(value ?? '').replace(/\D/g, '');

// T.C. Kimlik No: 11 hane, ilki 0 olamaz, iki basamaklı kontrol hanesi tutmalı.
function isValidTckn(value) {
  const digits = onlyDigits(value);
  if (!/^[1-9]\d{10}$/.test(digits)) return false;
  const d = digits.split('').map(Number);
  const odd = d[0] + d[2] + d[4] + d[6] + d[8];
  const even = d[1] + d[3] + d[5] + d[7];
  if ((((odd * 7 - even) % 10) + 10) % 10 !== d[9]) return false;
  const total = d.slice(0, 10).reduce((acc, digit) => acc + digit, 0);
  return total % 10 === d[10];
}

// Vergi Kimlik No: 10 hane, Maliye'nin standart kontrol algoritması.
function isValidVkn(value) {
  const digits = onlyDigits(value);
  if (!/^\d{10}$/.test(digits)) return false;
  const d = digits.split('').map(Number);
  let sum = 0;
  for (let i = 0; i < 9; i += 1) {
    const tmp = (d[i] + (9 - i)) % 10;
    sum += tmp === 9 ? tmp : (tmp * (2 ** (9 - i))) % 9;
  }
  return (10 - (sum % 10)) % 10 === d[9];
}

// Alan hem TCKN hem VKN kabul ediyor; hangisi olduğunu hane sayısı belirler.
const isValidTcknOrVkn = (value) => {
  const digits = onlyDigits(value);
  if (digits.length === 11) return isValidTckn(digits);
  if (digits.length === 10) return isValidVkn(digits);
  return false;
};

// DHL e-postada yalnızca ASCII kabul ediyor; "alıcı@test.com" gibi Türkçe karakterli
// adresler 7019 hatasıyla reddediliyordu.
const isValidEmail = (value) =>
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/.test(String(value || '').trim());

// Ülke kodu: "+90" biçiminde. CountryList'te Åland gibi alan koduyla birleşik
// 5 haneli kodlar da var ("+35818").
const isValidPhoneCode = (value) => /^\+\d{1,5}$/.test(String(value || '').trim());

// ABD/Kanada gibi ülkelerde sabit hat ve cep aynı aralıkta olduğundan kütüphane
// tipi ayırt edemiyor; bu durumda numara cep kabul edilir.
const MOBILE_PHONE_TYPES = ['MOBILE', 'FIXED_LINE_OR_MOBILE'];

// Telefon, seçilen ülke koduna göre libphonenumber-js ile doğrulanır.
// mobileOnly: gönderici için yalnızca cep numarası kabul edilir.
function isValidPhone(phone, phoneCode, { mobileOnly = false } = {}) {
  const parsed = parsePhone(onlyDigits(phone), phoneCode);
  if (!parsed || !parsed.isValid()) return false;
  return !mobileOnly || MOBILE_PHONE_TYPES.includes(parsed.getType());
}

// "2026-09-10" -> "10.09.2026". Geçersiz/boş değerde "-" döner.
function formatDate(value) {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

// PLT servisinin pltReason değerini kullanıcıya gösterilecek metne çevirir.
// "ok" başarı durumudur, burada karşılığı yok.
function getPltReasonText(plt) {
  switch (plt?.pltReason) {
    case 'country_not_in_list':
      return 'Bu ülke PLT listesinde yok.';
    case 'inbound_not_allowed':
      return 'Bu ülkeye gelen gönderilerde PLT geçerli değil.';
    case 'over_limit':
      return plt.limitValue
        ? `Beyan tutarı PLT sınırının (${plt.limitValue} ${plt.limitCurrency || ''}) üzerinde.`.replace(' )', ')')
        : 'Beyan tutarı PLT sınırının üzerinde.';
    case 'currency_unknown':
      return 'Beyan para birimi için kur bilgisi yok, PLT uygulanmıyor.';
    default:
      return '';
  }
}

const EMPTY_ITEM = { packageType: '', description: '', quantity: '', netWeight: '', unitValue: '', originCountry: '' };

// Formun başlangıç hali. Hem ilk yüklemede hem "Başka Bir Gönderi Oluştur"da
// buradan üretilir; iki yerde ayrı ayrı yazıldığında alanlar birbirinden kayıyordu
// (documentType sıfırlamada eksik kalıp undefined oluyordu).
const createEmptyFormData = () => ({
  sender: {
    name: '',
    company: '',
    country: 'TR',
    address1: '',
    address2: '',
    address3: '',
    city: '',
    postalCode: '',
    email: '',
    phoneCode: '+90',
    phone: '',
    tckn: '',
  },
  recipient: {
    name: '',
    company: '',
    country: '',
    address1: '',
    address2: '',
    address3: '',
    city: '',
    postalCode: '',
    email: '',
    phoneCode: '+90',
    phone: '',
    tckn: '',
  },
  shipmentType: { name: '', price: '' },
  content: '',
  customsInfo: '',
  docOrPackage: 'Paket',
  packageType: '',
  extraProtection: 'Evet',
  items: [],
  isPerformaNeed: false,
  weight: '',
  weightUnit: 'KG',
  length: '',
  width: '',
  height: '',
  contentReference: 'EASYPOINT',
  contentDescription: '',
  otherComments: '',
  documentType: '',
  quoteReceiverCountry: '',
  quoteReceiverCity: '',
  quoteReceiverPostalCode: '',
  quoteShipmentDate: new Date().toISOString().slice(0, 10),
  quoteShipmentValue: '',
  quoteCurrency: 'EUR',
  quoteInsuredValue: '',
  exportReason: 'PERMANENT', // DHL'de de varsayılan bu
});

export default function CargoOrderWizard() {
  const [activeTab, setActiveTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(createEmptyFormData);

  const [itemForm, setItemForm] = useState({
    packageType: '',
    description: '',
    quantity: '',
    netWeight: '',
    unitValue: '',
    originCountry: '',
  });
  // Kullanıcı ilk kez mi kullanıyor? (örnek state, gerçek uygulamada backend'den alınabilir)
  const [isFirstTimeUser] = useState(true); // false yaparsan "Kime" ile başlar

  // Fiyat sorgulama yapıldı mı?
  const [fiyatSorgulandi, setFiyatSorgulandi] = useState(false);
  // Fiyat sorgusundan sonra DHL'deki gibi araya giren "Ücret detayları" ekranı
  const [showRateDetails, setShowRateDetails] = useState(false);
  // Gönderinin fiziksel çıkış noktası: kullanıcının bağlı olduğu Easy Point
  const [senderPoint, setSenderPoint] = useState(null);
  const [promotionCode, setPromotionCode] = useState('');
  // Gönderi Türü adımının ikinci ekranı: "Bir ürün veya servis seçin"
  const [showProductSelection, setShowProductSelection] = useState(false);
  const [productSelectionWarning, setProductSelectionWarning] = useState('');
  // Gönderi Türü adımının üçüncü ekranı: seçilen ürünün ücret dökümü
  const [showRateSummary, setShowRateSummary] = useState(false);
  const [contentDescTooltipOpen, setContentDescTooltipOpen] = useState(false);
  // PLT (Paperless Trade) durumu servisten gelir: ülke + beyan tutarı birlikte belirler.
  const [pltInfo, setPltInfo] = useState(null);

  const [selectedService, setSelectedService] = useState(null);
  const [apiProducts, setApiProducts] = useState([]);
  const [exchangeRates, setExchangeRates] = useState(null);

  const [showSummary, setShowSummary] = useState(false);
  const [showBildirSection, setShowBildirSection] = useState(false);
  const [showKabulModal, setShowKabulModal] = useState(false);
  const [isKabulEdildi, setIsKabulEdildi] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [openAccordion, setOpenAccordion] = useState('details');
  const [creationDate, setCreationDate] = useState(null);
  const [kabulTarihi, setKabulTarihi] = useState(null);
  const [showNotAcceptedModal, setShowNotAcceptedModal] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  // "Gönderiyi Kabul Et" öncesi personel onayı: ödeme kontrol edildi mi, barkod yapıştırıldı mı
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [acceptChecks, setAcceptChecks] = useState({ paymentChecked: false, labelAttached: false });
  // Ödeme işaretlendiyse tekrar denemede yeniden çağrılmaz (uç "zaten ödendi" hatası dönüyor).
  const [isPaidMarked, setIsPaidMarked] = useState(false);
  const [descTooltipAnchor, setDescTooltipAnchor] = useState(null);
  const [showDescModal, setShowDescModal] = useState(false);
  const [descModalFields, setDescModalFields] = useState({
    product: '',
    brand: '',
    model: '',
    material: '',
    usage: '',
    description: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [shipmentResponse, setShipmentResponse] = useState(null);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // itemsOverride: gümrük adımında henüz state'e işlenmemiş öğe listesiyle doğrulama yapmak için.
  const validateStep = (step, itemsOverride) => {
    const newErrors = {};
    const { sender, recipient, contentDescription, contentReference, docOrPackage } = formData;
    const items = itemsOverride || formData.items;

    if (step === 0) {
      // Posta kodu olmayan ülkelerde (DHL listesi) adres yalnızca şehirle tanımlanır.
      const requiredSenderFields = ['name', 'company', 'country', 'address1', 'city', 'postalCode', 'email', 'phoneCode', 'phone', 'tckn']
        .filter(field => field !== 'postalCode' || !isNoPostalCodeCountry(sender.country));
      requiredSenderFields.forEach(field => {
        if (!sender[field]) newErrors[`sender_${field}`] = 'Bu alan zorunludur';
      });
      // Biçim kontrolleri yalnızca alan doluysa; boşta "zorunludur" mesajı kalsın.
      if (sender.email && !isValidEmail(sender.email)) {
        newErrors.sender_email = 'Geçerli bir e-posta adresi girin (Türkçe karakter kullanmayın)';
      }
      if (sender.phoneCode && !isValidPhoneCode(sender.phoneCode)) {
        newErrors.sender_phoneCode = 'Ülke kodu "+90" biçiminde olmalı';
      }
      if (sender.phone && !isValidPhone(sender.phone, sender.phoneCode, { mobileOnly: true })) {
        newErrors.sender_phone = 'Seçilen ülke koduna uygun geçerli bir cep telefonu girin';
      }
      if (sender.tckn && !isValidTcknOrVkn(sender.tckn)) {
        newErrors.sender_tckn = 'Geçerli bir TCKN (11 hane) veya VKN (10 hane) girin';
      }
    }

    if (step === 1) {
      // Alıcı e-postası opsiyonel; girilirse formatı kontrol edilir.
      const requiredRecipientFields = ['name', 'company', 'country', 'address1', 'city', 'postalCode', 'phoneCode', 'phone']
        .filter(field => field !== 'postalCode' || !isNoPostalCodeCountry(recipient.country));
      requiredRecipientFields.forEach(field => {
        if (!recipient[field]) newErrors[`recipient_${field}`] = 'Bu alan zorunludur';
      });
      if (recipient.email && !isValidEmail(recipient.email)) {
        newErrors.recipient_email = 'Geçerli bir e-posta adresi girin (Türkçe karakter kullanmayın)';
      }
      if (recipient.phoneCode && !isValidPhoneCode(recipient.phoneCode)) {
        newErrors.recipient_phoneCode = 'Ülke kodu "+49" gibi olmalı';
      }
      if (recipient.phone && !isValidPhone(recipient.phone, recipient.phoneCode)) {
        newErrors.recipient_phone = 'Seçilen ülke koduna uygun geçerli bir telefon numarası girin';
      }
      // Alıcı TCKN/VKN zorunlu değil; girildiyse doğru olmalı.
      if (recipient.tckn && !isValidTcknOrVkn(recipient.tckn)) {
        newErrors.recipient_tckn = 'Geçerli bir TCKN (11 hane) veya VKN (10 hane) girin';
      }
    }

    if (step === 3) {
      if (docOrPackage === 'Döküman') {
        // Dökümanda açıklama alanı ekranda yok; DHL de istemiyor.
        if (!formData.documentType) newErrors.documentType = 'Döküman tipi zorunludur';
      } else {
        // DHL tek kelimelik/kısa açıklamaları kabul etmiyor: en az 8 karakter ve 2 kelime.
        const description = String(contentDescription || '').trim();
        if (!description) {
          newErrors.contentDescription = 'Gönderi açıklaması zorunludur';
        } else if (description.length < 8 || description.split(/\s+/).length < 2) {
          newErrors.contentDescription = 'Gönderi açıklaması en az 8 karakter ve 2 kelime olmalı';
        }
      }
    }

    if (step === 4) {
      // Gümrük faturası alanları (fatura no, imzalayan kişi/unvan) ekrandan
      // kaldırıldı; payload'a boş gidiyor, bu yüzden burada kontrol edilmiyor.
      if (docOrPackage === 'Paket' && items.length === 0) {
        newErrors.items = 'Lütfen en az bir öğe ekleyin.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Gümrük öğesinin gönderilebilir olması için dolu olması gereken alanlar.
  const isItemComplete = (item) =>
    Boolean(item?.packageType && item?.description && item?.quantity && item?.netWeight && item?.unitValue);

  const downloadBase64File = (base64Data, fileName, fileType = 'application/pdf') => {
    // Base64 string'ini decode et
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: fileType });

    // İndirme linki oluştur
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;

    // Linki tıkla ve sonra kaldır
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fiyat, gönderinin DHL ağına girdiği noktadan hesaplanır; kullanıcının
  // bağlı olduğu Easy Point'in adresini bir kez çekip sorgularda kullanıyoruz.
  useEffect(() => {
    const user = tokenService.getUser();
    const poiID = user?.firstResponsibleOffice?.id;
    if (!poiID || !user?.token) {
      return;
    }
    Services.GetPoiDetails(user.token, poiID)
      .then((res) => {
        if (res.data.status && res.data.result) {
          setSenderPoint(res.data.result);
        }
      })
      .catch((error) => console.error('Nokta bilgisi alınamadı:', error));
  }, []);

  // Waybill ekranında PLT durumunu göstermek için alıcı ülkesi ve beyan tutarıyla sorgulanır.
  useEffect(() => {
    const countryCode = formData.recipient.country;
    if (!showBildirSection || !countryCode) {
      return;
    }
    Services.GetPLTCountries({
      countryCode,
      declaredValue: toNumber(formData.quoteShipmentValue),
      declaredValueCurrency: formData.quoteCurrency,
    })
      .then((res) => {
        if (!res.data.status) {
          setPltInfo(null);
          return;
        }
        const result = res.data.result || {};
        setPltInfo(result.items?.[0] || null);
        if (result.isExpired) {
          console.warn('PLT listesi güncel değil, geçerlilik:', result.validUntil);
        }
      })
      .catch((error) => {
        console.error('PLT bilgisi alınamadı:', error);
        setPltInfo(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBildirSection, formData.recipient.country]);

  // Son adımdan sonra DHL'deki gibi bir onay ekranı: özet gösterilir,
  // gönderi ancak oradaki "Bildir" ile oluşturulur.
  const goToSummary = () => {
    const lastStep = formData.docOrPackage === 'Paket' ? 4 : 3;

    // Ekranda doldurulmuş ama "Yeni öğe ekle" ile eklenmemiş öğe payload'a girmiyordu;
    // toplamlar onu saydığı için ekrandaki tutar ile gönderilen tutar farklı oluyordu.
    let { items } = formData;
    if (formData.docOrPackage === 'Paket' && isItemComplete(itemForm)) {
      items = [...items, itemForm];
      setFormData(prev => ({ ...prev, items }));
      setItemForm(EMPTY_ITEM);
    }

    if (validateStep(lastStep, items)) {
      setShowSummary(true);
    }
  };

  // DHL Gönderisi oluşturma fonksiyonu
  const handleCreateDHLCargo = async () => {

    if (!validateStep(0) || !validateStep(1) || !validateStep(3) || !validateStep(4)) {
      return;
    }

    setIsLoading(true);

    // Ortak zorunlu alanlar
    const baseData = {
      senderNameLastname: formData.sender.name,
      senderPhoneCountryCode: formData.sender.phoneCode,
      senderPhone: toNationalPhoneDigits(formData.sender.phone, formData.sender.phoneCode),
      senderCompany: formData.sender.company || '',
      senderEmail: formData.sender.email,
      senderAddress: formData.sender.address1,
      senderPostalCode: formData.sender.postalCode,
      senderCity: formData.sender.city,
      receiverCountryCode: formData.recipient.country,
      receiverPhoneCountryCode: formData.recipient.phoneCode,
      receiverPhone: toNationalPhoneDigits(formData.recipient.phone, formData.recipient.phoneCode),
      receiverNameLastname: formData.recipient.name,
      receiverCompany: formData.recipient.company || '',
      // Alıcı e-postası opsiyonel; boşsa alan hiç gönderilmez (DHL boş e-postayı reddediyor).
      ...(formData.recipient.email ? { receiverEmail: formData.recipient.email } : {}),
      receiverAddress: formData.recipient.address1,
      receiverPostalCode: formData.recipient.postalCode,
      receiverCity: formData.recipient.city,
      declaredValue: toNumber(formData.quoteShipmentValue),
      declaredValueCurrency: formData.quoteCurrency,
      // Fiyat sigortalı sorgulanıyordu ama gönderi sigortasız oluşuyordu; seçim buraya da
      // taşınmalı. Sigortalanan değer boşsa gönderi değeri kullanılır.
      withInsurance: formData.extraProtection === 'Evet',
      insuranceValue:
        formData.extraProtection === 'Evet'
          ? toNumber(formData.quoteInsuredValue) || toNumber(formData.quoteShipmentValue)
          : null,
    };

    let requestBody = {};

    // Seçilen servisin EUR fiyatını al
    const selectedPrice = selectedServiceObj?.totalPrice?.find(p => p.priceCurrency === 'EUR')?.price || 0;

    if (formData.docOrPackage === 'Döküman') {
      requestBody = {
        ...baseData,
        weight: toNumber(formData.weight),
        length: toNumber(formData.length),
        width: toNumber(formData.width),
        height: toNumber(formData.height),
        isDocument: true,
        paidPrice: selectedPrice,
        paidPriceCurrency: 'EUR',
        documentType: formData.documentType,
      };
    } else {
      // Paket için
      requestBody = {
        ...baseData,
        isCustomsDeclarable: true,
        labelDescription: formData.contentDescription || '',
        isDocument: false,
        paidPrice: selectedPrice,
        paidPriceCurrency: 'EUR',
        exportReason: formData.exportReason,
        // Prod'da backend boş alanları kendisi dolduruyor, ama stage doldurmuyor
        // ve DHL boş fatura alanlarını reddediyor (hata 7032). Bu yüzden değerleri
        // burada üretiyoruz. Backend her ortamda üretmeye başlarsa kaldırılabilir.
        exportInvoice: {
          date: new Date().toISOString().slice(0, 10),
          number: `EP${Date.now().toString().slice(-10)}`,
          signatureName: (formData.sender.name || 'Easy Point').slice(0, 35),
          signatureTitle: 'Manager',
        },
        // toNumber virgüllü girişi de okur ("12,50" -> 12.5); Number() bunu NaN yapıyordu.
        items: formData.items.length > 0 ? formData.items.map(item => ({
          customs: {
            quantity: toNumber(item.quantity) || 1,
            price: toNumber(item.unitValue),
            description: item.description || '',
          },
          weight: toNumber(item.netWeight) || 1,
          length: toNumber(formData.length) || 1,
          width: toNumber(formData.width) || 1,
          height: toNumber(formData.height) || 1,
          // Menşei ülke listeden seçildiği için zaten ISO kodu ("TR", "FR"); seçilmediyse TR.
          manufacturerCountry: item.originCountry || 'TR',
        })) : [
          {
            customs: {
              quantity: 1,
              price: toNumber(formData.quoteShipmentValue),
              description: formData.contentDescription || 'Product description'
            },
            weight: toNumber(formData.weight) || 1,
            length: toNumber(formData.length) || 1,
            width: toNumber(formData.width) || 1,
            height: toNumber(formData.height) || 1,
            manufacturerCountry: 'TR',
          }
        ],
      };
    }

    console.log("Request Body : ", requestBody);

    try {
      const response = await Services.CreateDHLCargo(requestBody);
      if (response.data && response.data.status) {
        setShipmentResponse(response.data.result);
        setCreationDate(new Date());
        setShowSummary(false);
        setShowBildirSection(true); // Waybill ve sonraki adımların olduğu ekran
      } else {
        alert('Gönderi oluşturulamadı. Lütfen bilgileri kontrol edin.');
      }
    } catch (error) {
      alert('Bir hata oluştu: ' + (error?.response?.data?.message || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Sıfırlama fonksiyonu
  const handleResetAll = () => {
    setFormData(createEmptyFormData());
    setItemForm(EMPTY_ITEM);
    setActiveStep(0);
    setActiveTab(0);
    setFiyatSorgulandi(false);
    setShowRateDetails(false);
    setShowProductSelection(false);
    setShowRateSummary(false);
    setProductSelectionWarning('');
    setShowSummary(false);
    setShowBildirSection(false);
    setSelectedService(null);
    setIsKabulEdildi(false);
    setKabulTarihi(null);
    // Önceki gönderiden kalan veriler yeni gönderiye taşınmasın.
    setApiProducts([]);
    setExchangeRates(null);
    setShipmentResponse(null);
    setCreationDate(null);
    setPromotionCode('');
    setErrors({});
    setPltInfo(null);
    setIsPaidMarked(false);
    setShowAcceptConfirm(false);
  };

  // Gönderi açıklaması gümrük öğesinin Tanım alanına aktarılır. Kullanıcı Tanım'ı
  // kendi değiştirdiyse üzerine yazılmaz; hâlâ son aktarılan metinse güncellenir.
  const lastCopiedDescriptionRef = useRef('');
  const goToCustomsStep = () => {
    const description = String(formData.contentDescription || '').trim();
    setItemForm(prev =>
      !prev.description || prev.description === lastCopiedDescriptionRef.current
        ? { ...prev, description }
        : prev
    );
    lastCopiedDescriptionRef.current = description;
    setActiveStep(4);
  };

  const handleBackFromSummary = () => {
    setShowSummary(false);
    if (formData.docOrPackage === 'Paket') {
      setActiveStep(4); // Gümrük Bilgileri adımı
    } else {
      setActiveStep(3); // Gönderi İçeriği adımı
    }
  };

  // base64 PDF'i gizli bir iframe'e yükleyip tarayıcının yazdırma penceresini açar.
  // Yazdırma penceresi kapandığında (ya da tarayıcı haber vermezse zaman aşımıyla)
  // resolve olur; böylece birden fazla belge sırayla yazdırılabilir.
  const printBase64Pdf = (base64Data, jobName) =>
    new Promise((resolve) => {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blobUrl = URL.createObjectURL(new Blob([byteNumbers], { type: 'application/pdf' }));

      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = blobUrl;

      let settled = false;
      let onFocusBack = null;
      // Chrome yazdırma işine (ve "PDF olarak kaydet" dosya adına) sayfa başlığını verir;
      // yazdırma boyunca başlığı belgenin adıyla değiştirip sonra geri alıyoruz.
      const previousTitle = document.title;
      const cleanup = () => {
        if (settled) return;
        settled = true;
        document.title = previousTitle;
        if (onFocusBack) {
          window.removeEventListener('focus', onFocusBack);
        }
        URL.revokeObjectURL(blobUrl);
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
        resolve();
      };

      iframe.onload = () => {
        try {
          if (jobName) {
            document.title = jobName;
          }
          iframe.contentWindow.focus();
          // Chrome'un iframe içindeki PDF görüntüleyicisi onafterprint tetiklemiyor.
          // Yazdırma penceresi kapanınca odak ana pencereye döndüğü için onu da dinliyoruz;
          // yoksa promise çözülmeyip sonraki belge dakikalar sonra açılıyor.
          const openedAt = Date.now();
          onFocusBack = () => {
            // print() çağrısıyla aynı anda gelen odak olaylarını yok say.
            if (Date.now() - openedAt < 500) return;
            setTimeout(cleanup, 300);
          };
          window.addEventListener('focus', onFocusBack);
          iframe.contentWindow.onafterprint = () => setTimeout(cleanup, 300);
          iframe.contentWindow.print();
          // Hiçbir sinyal gelmezse belge yine de temizlensin.
          setTimeout(cleanup, 60000);
        } catch (error) {
          console.error('Yazdırma penceresi açılamadı:', error);
          // Yazdırma engellenirse belge en azından indirilsin.
          downloadBase64File(base64Data, 'gonderi_belgesi.pdf');
          cleanup();
        }
      };

      document.body.appendChild(iframe);
    });

  // "Gönderiyi Kabul Et" — gönderiyi noktanın envanterine kaydeder ve kuryeyi çağırır.
  // Uç eski BPIShipmentQuery'ye gidiyor; yeni oluşan gönderi orada henüz görünmezse
  // "Post not found" döner, o yüzden başarısızlıkta kabul edildi durumuna geçmiyoruz.
  // "Gönderiyi Kabul Et" butonu önce onay modalını açar.
  const openAcceptConfirm = () => {
    setAcceptChecks({ paymentChecked: false, labelAttached: false });
    setShowAcceptConfirm(true);
  };

  // Onay sonrası: gönderi ödendi olarak işaretlenir, ardından DHL drop-off kabulü yapılır
  // (envantere kayıt açılır ve kurye çağrılır). Ödeme işaretlenip kabul başarısız olursa
  // tekrar denemede yalnızca kabul adımı çalışır.
  const handleAcceptShipment = async () => {
    const waybill = shipmentResponse?.shipmentTrackingNumber;
    if (!waybill) {
      setShowAcceptConfirm(false);
      setErrorMessage('Waybill numarası bulunamadı, gönderi kabul edilemedi.');
      setShowErrorModal(true);
      return;
    }

    const user = tokenService.getUser();
    setIsAccepting(true);
    try {
      if (!isPaidMarked) {
        const paidRes = await Services.SetDHLShipmentPaid(user.token, waybill, 1);
        if (!paidRes?.data?.status) {
          setShowAcceptConfirm(false);
          setErrorMessage(paidRes?.data?.message || 'Ödeme kaydedilemedi. Lütfen tekrar deneyin.');
          setShowErrorModal(true);
          return;
        }
        setIsPaidMarked(true);
      }

      const res = await Services.DHLDropOffAccept(user.token, waybill);
      if (!res?.data?.status) {
        setShowAcceptConfirm(false);
        setErrorMessage(
          `Ödeme kaydedildi ancak gönderi kabul edilemedi: ${res?.data?.message || 'bilinmeyen hata'}. Lütfen tekrar deneyin.`
        );
        setShowErrorModal(true);
        return;
      }

      setShowAcceptConfirm(false);
      setIsKabulEdildi(true);
      setKabulTarihi(new Date());
      setShowKabulModal(true);

    } catch (error) {
      console.error('Gönderi kabul edilemedi:', error);
      setShowAcceptConfirm(false);
      setErrorMessage(
        error?.response?.data?.message || 'Gönderi kabul edilirken bir sorun oluştu. Lütfen tekrar deneyin.'
      );
      setShowErrorModal(true);
    } finally {
      setIsAccepting(false);
    }
  }

  // Etiket ve fatura tek PDF'te birleştirilip tek yazdırma penceresinde açılır.
  // pdf-lib yalnızca yazdırırken yüklenir; makbuz alttaki bağlantıdan indirilir.
  const mergeBase64Pdfs = async (base64List) => {
    const { PDFDocument } = await import('pdf-lib');
    const merged = await PDFDocument.create();
    for (const base64 of base64List) {
      // eslint-disable-next-line no-await-in-loop
      const source = await PDFDocument.load(base64);
      // eslint-disable-next-line no-await-in-loop
      const pages = await merged.copyPages(source, source.getPageIndices());
      pages.forEach(page => merged.addPage(page));
    }
    return merged.saveAsBase64();
  }

  const printDocuments = async () => {
    const documents = shipmentResponse?.documents || [];
    const label = documents.find(doc => doc.typeCode === 'label');

    if (!label?.content) {
      alert('Yazdırılacak belge bulunamadı.');
      return;
    }

    // Fatura ayrı gelmiyorsa (eski yanıtlar) faturayı içeren birleşik belge kullanılır.
    const invoice =
      documents.find(doc => doc.typeCode === 'invoice' && doc.content) ||
      documents.find(doc => doc.typeCode === 'invoice+receipt' && doc.content);

    let content = label.content;
    if (invoice) {
      try {
        content = await mergeBase64Pdfs([label.content, invoice.content]);
      } catch (error) {
        // Birleştirilemezse en azından etiket yazdırılsın.
        console.error('Belgeler birleştirilemedi:', error);
      }
    }

    const waybill = shipmentResponse?.shipmentTrackingNumber;
    await printBase64Pdf(content, waybill ? `Gonderi_Belgeleri_${waybill}` : 'Gonderi_Belgeleri');
  }

  // "Gerekli Belgeleri Yazdır" — gönderi kabul edilmeden yazdırmaya izin verilmez.
  const handlePrintDocuments = () => {
    if (!isKabulEdildi) {
      setShowNotAcceptedModal(true);
      return;
    }
    printDocuments();
  }

  // DHL fatura ile makbuzu ayrı (invoice, receipt) döndürüyor; eski gönderilerde ikisi
  // tek PDF'te (invoice+receipt) geliyor. Bağlantılar gelen belgelere göre gösterilir.
  const downloadLinks = [
    { typeCode: 'invoice', label: 'Faturayı İndir', fileName: 'fatura.pdf' },
    { typeCode: 'receipt', label: 'Makbuzu İndir', fileName: 'gonderi-makbuzu.pdf' },
    { typeCode: 'invoice+receipt', label: 'Fatura ve Makbuzu İndir', fileName: 'fatura-ve-makbuz.pdf' },
  ].filter(link => shipmentResponse?.documents?.some(doc => doc.typeCode === link.typeCode && doc.content));

  const downloadDocument = ({ typeCode, fileName }) => {
    const document = shipmentResponse?.documents?.find(doc => doc.typeCode === typeCode);
    if (document?.content) {
      downloadBase64File(document.content, fileName);
    } else {
      alert("İndirilecek belge bulunamadı.");
    }
  }



  // DHL'deki "Temizle" — gönderici formunu boşaltır, ülke sabit kalır.
  const handleClearSender = () => {
    setFormData(prev => ({
      ...prev,
      sender: {
        ...prev.sender,
        name: '',
        company: '',
        address1: '',
        address2: '',
        address3: '',
        city: '',
        postalCode: '',
        email: '',
        phone: '',
        tckn: '',
      },
    }));
    setErrors({});
  };

  // Firma ismi ad soyadla aynı başlıyor. Kullanıcı firmayı kendi değiştirdiyse
  // (değerler ayrıştıysa) yansıtmayı bırakırız; alanı boşaltırsa tekrar yansımaya başlar.
  const handlePartyNameChange = (party, value) => {
    setFormData(prev => {
      const current = prev[party];
      const mirrored = !current.company || current.company === current.name;
      return {
        ...prev,
        [party]: { ...current, name: value, company: mirrored ? value : current.company },
      };
    });
  };

  // DHL'de sigortalanan değer gönderi değeriyle aynı başlıyor. Kullanıcı sigortalanan
  // değeri kendi değiştirdiyse (değerler ayrıştıysa) yansıtmayı bırakırız; alanı
  // boşaltırsa tekrar yansımaya başlar.
  const handleShipmentValueChange = (value) => {
    setFormData(prev => {
      const mirrored =
        prev.extraProtection === 'Evet' &&
        (!prev.quoteInsuredValue || prev.quoteInsuredValue === prev.quoteShipmentValue);
      return {
        ...prev,
        quoteShipmentValue: value,
        quoteInsuredValue: mirrored ? value : prev.quoteInsuredValue,
      };
    });
  };

  // Sigorta sonradan açılırsa alan boşsa gönderi değeriyle dolsun.
  const handleExtraProtectionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      extraProtection: value,
      quoteInsuredValue:
        value === 'Evet' && !prev.quoteInsuredValue ? prev.quoteShipmentValue : prev.quoteInsuredValue,
    }));
  };

  const selectedPackageType = findPackageType(formData.packageType);

  // Paket türü seçilince ölçü ve ağırlık o ambalajın sabit değerlerinden dolar.
  // Zarfta ağırlığı kullanıcı girdiği için ambalaj ağırlığı yalnızca başlangıç değeri.
  const handlePackageTypeChange = (value) => {
    const type = findPackageType(value);
    setFormData(prev => ({
      ...prev,
      packageType: value,
      ...(type
        ? {
            length: String(type.length),
            width: String(type.width),
            height: String(type.height),
            weight: String(type.weight),
            weightUnit: 'KG',
          }
        : {}),
    }));
  };

  // Fiyat Sorgula butonuna tıklanınca
  // Fiyat sorgusu payload'ı; hem ilk sorguda hem "Ön Fiyat Al" adımında kullanılır.
  const buildRateRequest = () => {
    // Alıcı adımı doldurulduktan sonra geçerli adres odur; kullanıcı orada ülkeyi
    // değiştirirse fiyat da yeni ülkeye göre sorulmalı. İlk sorguda alıcı bilgisi
    // henüz yok, sorgu ekranındaki alanlar kullanılır.
    const receiverCountry = formData.recipient.country || formData.quoteReceiverCountry;
    const receiverCity = formData.recipient.city || formData.quoteReceiverCity;
    const receiverPostalCode = formData.recipient.postalCode || formData.quoteReceiverPostalCode;

    return {
      // Nokta bilgisi gelmediyse eski sabit adres devrede kalır.
      senderAddress: senderPoint?.muhaberatAddress1 || 'İkitelli OSB Mah. 10. Cad. No:12',
      senderPostalCode: senderPoint?.postalCode || '34306',
      senderCity: senderPoint?.city || 'İstanbul',
      receiverAddress: receiverCity,
      receiverPostalCode: receiverPostalCode,
      receiverCity: receiverCity,
      receiverCountryCode: receiverCountry,
      declaredValue: toNumber(formData.quoteShipmentValue),
      declaredValueCurrency: formData.quoteCurrency,
      weight: toNumber(formData.weight),
      length: toNumber(formData.length),
      width: toNumber(formData.width),
      height: toNumber(formData.height),
      withInsurance: formData.extraProtection === 'Evet',
      isDocument: formData.docOrPackage === 'Döküman',
    };
  };

  // Fiyat sorgusu hataları sessiz kalmasın; DHL'in mesajı varsa onu gösteriyoruz.
  const showRateError = (response, error) => {
    const message =
      response?.data?.message ||
      error?.response?.data?.message ||
      'Fiyat bilgisi alınamadı. Lütfen bilgileri kontrol edip tekrar deneyin.';
    setErrorMessage(message);
    setShowErrorModal(true);
  };

  // "Ön Fiyat Al": Gönderi Türü adımındaki değişiklikleri fiyata yansıtır.
  const handleOnFiyatAl = async () => {
    setIsLoading(true);
    try {
      const response = await Services.GetDHLCargoRates(buildRateRequest());
      if (!response.data.status || !response.data.result) {
        showRateError(response);
        return;
      }
      const products = response.data.result.products || [];
      setApiProducts(products);
      setExchangeRates(response.data.result.exchangeRates);
      if (products.length === 0) {
        showRateError(response);
        return;
      }
      if (!products.some(p => p.productName === selectedService)) {
        setSelectedService(products[0].productName);
      }
      setProductSelectionWarning('');
      setShowProductSelection(true);
    } catch (error) {
      console.error('Ön fiyat alma hatası:', error);
      showRateError(null, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFiyatSorgula = async () => {
    setIsLoading(true);
    try {
      const response = await Services.GetDHLCargoRates(buildRateRequest());
      if (!response.data.status || !response.data.result) {
        console.error('Fiyat alınamadı:', response.data);
        setApiProducts([]);
        setSelectedService(null);
        showRateError(response);
        return;
      }

      const products = response.data.result.products || [];
      setApiProducts(products);
      setExchangeRates(response.data.result.exchangeRates);

      if (products.length === 0) {
        setSelectedService(null);
        showRateError(response);
        return;
      }

      // Otomatik olarak ilk gönderi tipini seç
      const firstProduct = products[0];
      setSelectedService(firstProduct.productName);
      setFormData(prev => ({
        ...prev,
        shipmentType: {
          name: firstProduct.productName,
          price: firstProduct.totalPrice?.[0]?.price ?? '',
        },
      }));

      setActiveTab(1);
      setActiveStep(isFirstTimeUser ? 0 : 1);
      setFiyatSorgulandi(true);
      setShowRateDetails(true);
    } catch (error) {
      console.error('Fiyat sorgulama hatası:', error);
      setApiProducts([]);
      setSelectedService(null);
      showRateError(null, error);
    } finally {
      setIsLoading(false);
    }
  };

  // Gümrük adımındaki toplamlar: eklenen öğeler + hâlâ düzenlenen öğe
  const customsTotals = (() => {
    const rows = [...formData.items, itemForm];
    const num = (v) => Number(String(v || '').replace(',', '.')) || 0;
    const value = rows.reduce((acc, row) => acc + num(row.unitValue) * (num(row.quantity) || 0), 0);
    const weight = rows.reduce((acc, row) => acc + num(row.netWeight) * (num(row.quantity) || 0), 0);
    return {
      value: Number(value.toFixed(2)),
      weight: Number(weight.toFixed(2)),
    };
  })();

  // Gümrük adımına gelindiğinde öğe alanlarını gönderi bilgisinden doldur:
  // adet 1, net ağırlık seçilen kutunun ağırlığı, birim değeri gönderi değeri.
  // Yalnızca boş alanlar doldurulur, kullanıcının yazdığı bozulmaz.
  useEffect(() => {
    if (activeStep !== 4 || formData.docOrPackage !== 'Paket') {
      return;
    }
    setItemForm((prev) => ({
      ...prev,
      quantity: prev.quantity || '1',
      netWeight: prev.netWeight || String(formData.weight || ''),
      unitValue: prev.unitValue || String(formData.quoteShipmentValue || ''),
    }));
  }, [activeStep, formData.docOrPackage, formData.weight, formData.quoteShipmentValue]);

  const steps = getSteps(formData.docOrPackage);
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={2}>
            {/* DHL'de telefon en üstte, ülke ise sabit (gönderici her zaman Türkiye) */}
            <Stack direction="row" spacing={2}>
              <DialCodeSelect
                label="Kod *"
                sx={{ maxWidth: 160 }}
                value={formData.sender.phoneCode}
                onChange={code => setFormData(prev => ({ ...prev, sender: { ...prev.sender, phoneCode: code } }))}
                error={!!errors.sender_phoneCode}
                helperText={errors.sender_phoneCode}
              />
              <TextField
                label="Cep Telefonu *"
                placeholder="Cep Telefonu"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={formatPhoneInput(formData.sender.phone, formData.sender.phoneCode)}
                inputProps={{ inputMode: 'tel' }}
                onChange={e => setFormData(prev => ({ ...prev, sender: { ...prev.sender, phone: limitPhoneInput(e.target.value, prev.sender.phone, prev.sender.phoneCode) } }))}
                error={!!errors.sender_phone}
                helperText={errors.sender_phone}
              />
            </Stack>
            <TextField label="Ad, Soyad *" placeholder="Kişi Adı" InputLabelProps={{ shrink: true }} fullWidth value={formData.sender.name} onChange={e => handlePartyNameChange('sender', e.target.value)} error={!!errors.sender_name} helperText={errors.sender_name} />
            <TextField label="Firma İsmi *" placeholder="Firma İsmi" InputLabelProps={{ shrink: true }} fullWidth value={formData.sender.company} onChange={e => setFormData(prev => ({ ...prev, sender: { ...prev.sender, company: e.target.value } }))} error={!!errors.sender_company} helperText={errors.sender_company} />
            <CountryAutocomplete
              label="Ülkesinden *"
              disabled
              value={formData.sender.country}
              onChange={code => setFormData(prev => ({
                ...prev,
                sender: {
                  ...prev.sender,
                  country: code,
                  postalCode: isNoPostalCodeCountry(code) ? '' : prev.sender.postalCode,
                  phoneCode: getDialCode(code) || prev.sender.phoneCode,
                },
              }))}
              error={!!errors.sender_country}
              helperText={errors.sender_country}
            />
            <TextField label="Adres satırı 1 *" placeholder="Adres satırı 1" InputLabelProps={{ shrink: true }} fullWidth value={formData.sender.address1} onChange={e => setFormData(prev => ({ ...prev, sender: { ...prev.sender, address1: e.target.value } }))} error={!!errors.sender_address1} helperText={errors.sender_address1} />
            <TextField label="Adres satırı 2" placeholder="Adres satırı 2" InputLabelProps={{ shrink: true }} fullWidth value={formData.sender.address2} onChange={e => setFormData(prev => ({ ...prev, sender: { ...prev.sender, address2: e.target.value } }))} />
            <TextField label="Adres satırı 3" placeholder="Adres satırı 3" InputLabelProps={{ shrink: true }} fullWidth value={formData.sender.address3} onChange={e => setFormData(prev => ({ ...prev, sender: { ...prev.sender, address3: e.target.value } }))} />
            <CityAutocomplete
              label="Şehir *"
              placeholder="ŞEHİR"
              countryCode={formData.sender.country}
              addressType="pickup"
              value={formData.sender.city}
              onChange={city => setFormData(prev => ({ ...prev, sender: { ...prev.sender, city } }))}
              error={!!errors.sender_city}
              helperText={errors.sender_city}
            />
            <PostalCodeAutocomplete
              label={isNoPostalCodeCountry(formData.sender.country) ? 'Posta Kodu' : 'Posta Kodu *'}
              disabled={isNoPostalCodeCountry(formData.sender.country)}
              countryCode={formData.sender.country}
              value={formData.sender.postalCode}
              onChange={({ postalCode, city }) => setFormData(prev => ({
                ...prev,
                sender: { ...prev.sender, postalCode, city: city || prev.sender.city },
              }))}
              error={!!errors.sender_postalCode}
              helperText={errors.sender_postalCode || (isNoPostalCodeCountry(formData.sender.country) ? 'Bu ülkede posta kodu kullanılmıyor.' : undefined)}
            />
            <TextField label="e-posta adresi *" placeholder="e-posta adresi" InputLabelProps={{ shrink: true }} fullWidth value={formData.sender.email} onChange={e => setFormData(prev => ({ ...prev, sender: { ...prev.sender, email: e.target.value } }))} error={!!errors.sender_email} helperText={errors.sender_email} />
            <TextField label="Gönderici TCKN/VKN *" InputLabelProps={{ shrink: true }} fullWidth inputProps={{ maxLength: 11, inputMode: 'numeric' }} value={formData.sender.tckn} onChange={e => setFormData(prev => ({ ...prev, sender: { ...prev.sender, tckn: onlyDigits(e.target.value) } }))} error={!!errors.sender_tckn} helperText={errors.sender_tckn} />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" color="error" sx={{ width: 160, height: 48, fontWeight: 600 }} onClick={handleClearSender}>
                Temizle
              </Button>
              <Button variant="contained" color="error" sx={{ width: 160, height: 48, backgroundColor: '#DA3831', fontWeight: 600 }} onClick={() => { if (validateStep(0)) { setActiveStep(1) } }}>
                Sonraki
              </Button>
            </Stack>
          </Stack>
        );
      case 1:
        return (
          <Stack spacing={2}>
            <TextField label="Ad, Soyad *" placeholder="Kişi Adı" InputLabelProps={{ shrink: true }} fullWidth value={formData.recipient.name} onChange={e => handlePartyNameChange('recipient', e.target.value)} error={!!errors.recipient_name} helperText={errors.recipient_name} />
            <TextField label="Firma İsmi *" placeholder="Firma İsmi" InputLabelProps={{ shrink: true }} fullWidth value={formData.recipient.company} onChange={e => setFormData(prev => ({ ...prev, recipient: { ...prev.recipient, company: e.target.value } }))} error={!!errors.recipient_company} helperText={errors.recipient_company} />
            <CountryAutocomplete
              label="Ülkesine *"
              value={formData.recipient.country}
              onChange={code => setFormData(prev => ({
                ...prev,
                recipient: {
                  ...prev.recipient,
                  country: code,
                  postalCode: '',
                  city: '',
                  phoneCode: getDialCode(code) || prev.recipient.phoneCode,
                },
              }))}
              error={!!errors.recipient_country}
              helperText={errors.recipient_country}
            />
            <TextField label="Adres satırı 1 *" placeholder="Adres satırı 1" InputLabelProps={{ shrink: true }} fullWidth value={formData.recipient.address1} onChange={e => setFormData(prev => ({ ...prev, recipient: { ...prev.recipient, address1: e.target.value } }))} error={!!errors.recipient_address1} helperText={errors.recipient_address1} />
            <TextField label="Adres satırı 2" placeholder="Adres satırı 2" InputLabelProps={{ shrink: true }} fullWidth value={formData.recipient.address2} onChange={e => setFormData(prev => ({ ...prev, recipient: { ...prev.recipient, address2: e.target.value } }))} />
            <TextField label="Adres satırı 3" placeholder="Adres satırı 3" InputLabelProps={{ shrink: true }} fullWidth value={formData.recipient.address3} onChange={e => setFormData(prev => ({ ...prev, recipient: { ...prev.recipient, address3: e.target.value } }))} />
            <CityAutocomplete
              label="Şehir *"
              placeholder="ŞEHİR"
              countryCode={formData.recipient.country}
              addressType="delivery"
              value={formData.recipient.city}
              onChange={city => setFormData(prev => ({ ...prev, recipient: { ...prev.recipient, city } }))}
              error={!!errors.recipient_city}
              helperText={errors.recipient_city}
            />
            <PostalCodeAutocomplete
              label={isNoPostalCodeCountry(formData.recipient.country) ? 'Posta Kodu' : 'Posta Kodu *'}
              disabled={isNoPostalCodeCountry(formData.recipient.country)}
              countryCode={formData.recipient.country}
              value={formData.recipient.postalCode}
              onChange={({ postalCode, city }) => setFormData(prev => ({
                ...prev,
                recipient: { ...prev.recipient, postalCode, city: city || prev.recipient.city },
              }))}
              error={!!errors.recipient_postalCode}
              helperText={errors.recipient_postalCode || (isNoPostalCodeCountry(formData.recipient.country) ? 'Bu ülkede posta kodu kullanılmıyor.' : undefined)}
            />
            <TextField label="e-posta adresi" placeholder="e-posta adresi" InputLabelProps={{ shrink: true }} fullWidth value={formData.recipient.email} onChange={e => setFormData(prev => ({ ...prev, recipient: { ...prev.recipient, email: e.target.value } }))} error={!!errors.recipient_email} helperText={errors.recipient_email} />
            <Stack direction="row" spacing={2}>
              <DialCodeSelect
                label="Kod *"
                sx={{ maxWidth: 160 }}
                value={formData.recipient.phoneCode}
                onChange={code => setFormData(prev => ({ ...prev, recipient: { ...prev.recipient, phoneCode: code } }))}
                error={!!errors.recipient_phoneCode}
                helperText={errors.recipient_phoneCode}
              />
              <TextField
                label="Cep Telefonu *"
                placeholder="Cep Telefonu"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={formatPhoneInput(formData.recipient.phone, formData.recipient.phoneCode)}
                inputProps={{ inputMode: 'tel' }}
                onChange={e => setFormData(prev => ({ ...prev, recipient: { ...prev.recipient, phone: limitPhoneInput(e.target.value, prev.recipient.phone, prev.recipient.phoneCode) } }))}
                error={!!errors.recipient_phone}
                helperText={errors.recipient_phone}
              />
            </Stack>
            <TextField label="Alıcı TCKN/VKN" InputLabelProps={{ shrink: true }} fullWidth inputProps={{ maxLength: 11, inputMode: 'numeric' }} value={formData.recipient.tckn} onChange={e => setFormData(prev => ({ ...prev, recipient: { ...prev.recipient, tckn: onlyDigits(e.target.value) } }))} error={!!errors.recipient_tckn} helperText={errors.recipient_tckn} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button sx={{ width: "100%", maxWidth: 235, height: 48, fontWeight: 600 }} variant="outlined" color="error" onClick={() => setActiveStep((prev) => prev - 1)}>
                Önceki
              </Button>
              <Button sx={{ width: "100%", maxWidth: 235, height: 48, fontWeight: 600, backgroundColor: '#DA3831' }} variant="contained" color="error" onClick={() => { if (validateStep(1)) { setActiveStep(2) } }}>
                Sonraki
              </Button>
            </Box>
          </Stack>
        );
      case 2:
        // Ürün seçildikten sonraki ücret dökümü ekranı
        if (showRateSummary && selectedServiceObj) {
          const totalPrices = selectedServiceObj.totalPrice || [];
          const breakdowns = selectedServiceObj.detailedPriceBreakdown || [];
          const eurTotal = totalPrices.find(t => t.priceCurrency === 'EUR') || totalPrices[0] || {};
          const eurBreakdown = breakdowns.find(b => b.priceCurrency === 'EUR') || breakdowns[0];
          // TRY karşılıklarını da DHL döndürüyor (PULCL bloğu); kalem adına göre eşleştiriyoruz.
          const tryBreakdown = breakdowns.find(b => b.priceCurrency === 'TRY');
          const rows = (eurBreakdown?.breakdown || []).map(item => {
            const tax = item.priceBreakdown?.find(x => x.priceType === 'TAX');
            const tryItem = tryBreakdown?.breakdown?.find(x => x.name === item.name);
            return {
              name: item.name,
              base: tax?.basePrice ?? item.price,
              tax: tax?.price ?? 0,
              total: item.price,
              tryTotal: tryItem?.price ?? 0,
            };
          });
          const sum = (key) => rows.reduce((acc, row) => acc + Number(row[key] || 0), 0);
          const currency = eurBreakdown?.priceCurrency || 'EUR';
          // DHL TRY tutarlarını bu kurdan çeviriyor; yanıtta hazır geliyor.
          const exchangeRate = exchangeRates?.find(
            rate => rate.currency === currency && rate.baseCurrency === 'TRY'
          )?.currentExchangeRate;
          const delivery = selectedServiceObj.deliveryCapabilities?.estimatedDeliveryDateAndTime;
          return (
            <Stack spacing={2}>
              <Typography variant="h6">Ücret detayları</Typography>
              <Typography variant="h4" sx={{ color: '#DA3831', fontWeight: 700 }}>
                {eurTotal.priceCurrency} {eurTotal.price}
              </Typography>
              <Box sx={{ backgroundColor: '#DA3831', color: '#FFF', px: 2, py: 1.25, borderRadius: 1, alignSelf: 'flex-start', fontWeight: 700 }}>
                {selectedServiceObj.productName}
              </Box>
              {delivery && (
                <Box sx={{ mt: 2 }}>
                  <Typography>Tahmini Teslimat Tarihi</Typography>
                  <Typography>
                    {new Date(delivery).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })},
                  </Typography>
                  <Typography>
                    {new Date(delivery).toLocaleDateString('tr-TR', { weekday: 'long' })},{' '}
                    <span style={{ color: '#DA3831' }}>{getDeliveryTimeText(delivery)}</span>
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, border: '1px solid #E5E7EB', borderRadius: 1, p: 1.5, mt: 3 }}>
                <TextField
                  label="Promosyon Kodu"
                  placeholder="Promosyon Kodu"
                  InputLabelProps={{ shrink: true }}
                  variant="standard"
                  fullWidth
                  InputProps={{ disableUnderline: true }}
                  value={promotionCode}
                  onChange={(e) => setPromotionCode(e.target.value)}
                />
                <Button
                  variant="contained"
                  color="error"
                  disabled
                  startIcon={<Icon icon="mdi:gift-outline" width={20} height={20} />}
                  sx={{ backgroundColor: '#DA3831', minWidth: 140, height: 44, fontWeight: 600 }}
                >
                  Uygula
                </Button>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', fontWeight: 500, pb: 1 }}>
                  <Box sx={{ flex: 2 }}>Öğe</Box>
                  <Box sx={{ flex: 1 }}>Tutar</Box>
                  <Box sx={{ flex: 1 }}>KDV / Vergi</Box>
                  <Box sx={{ flex: 1 }}>Ücret detayları</Box>
                  <Box sx={{ flex: 1 }}>Karşılığı</Box>
                </Box>
                {rows.map(row => (
                  <Box key={row.name} sx={{ display: 'flex', py: 1, backgroundColor: '#FAFAFA' }}>
                    <Box sx={{ flex: 2 }}>{row.name}</Box>
                    <Box sx={{ flex: 1 }}>{formatMoney(row.base, currency)}</Box>
                    <Box sx={{ flex: 1 }}>{formatMoney(row.tax, currency)}</Box>
                    <Box sx={{ flex: 1 }}>{formatMoney(row.total, currency)}</Box>
                    <Box sx={{ flex: 1 }}>{formatTry(row.tryTotal)}</Box>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', py: 1, borderTop: '2px solid #111' }}>
                  <Box sx={{ flex: 2 }}>Toplam</Box>
                  <Box sx={{ flex: 1 }}>{formatMoney(sum('base'), currency)}</Box>
                  <Box sx={{ flex: 1 }}>{formatMoney(sum('tax'), currency)}</Box>
                  <Box sx={{ flex: 1 }}>{formatMoney(sum('total'), currency)}</Box>
                  <Box sx={{ flex: 1 }}>{formatTry(sum('tryTotal'))}</Box>
                </Box>
                {exchangeRate && (
                  <Typography sx={{ mt: 1.5, color: '#6B7280', fontSize: 14 }}>
                    1 {CURRENCY_SYMBOLS[currency] || currency} = {exchangeRate} ₺
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button sx={{ width: "100%", maxWidth: 235, height: 48, fontWeight: 600 }} variant="outlined" color="error" onClick={() => { setShowRateSummary(false); setShowProductSelection(true); }}>
                  Geri
                </Button>
                <Button
                  sx={{ width: "100%", maxWidth: 235, height: 48, fontWeight: 600, backgroundColor: '#DA3831' }}
                  variant="contained"
                  color="error"
                  onClick={() => { setShowRateSummary(false); setActiveStep((prev) => prev + 1); }}
                >
                  Sonraki
                </Button>
              </Box>
            </Stack>
          );
        }
        // "Ön Fiyat Al" sonrası ürün seçim ekranı
        if (showProductSelection) {
          return (
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ mb: 1 }}>Bir ürün veya servis seçin</Typography>
              {productSelectionWarning && (
                <Box sx={{ backgroundColor: '#FFCC00', color: '#111', px: 2, py: 1.5, borderRadius: 1, fontWeight: 600 }}>
                  {productSelectionWarning}
                </Box>
              )}
              {apiProducts.map((service) => {
                const selected = selectedService === service.productName;
                const priceInfo = service.totalPrice.find(p => p.priceCurrency === 'EUR');
                // DHL fiyatı iki para biriminde döner; TL karşılığı hazır geliyor.
                const tryPrice = service.totalPrice.find(p => p.priceCurrency === 'TRY');
                const delivery = service.deliveryCapabilities?.estimatedDeliveryDateAndTime;
                return (
                  <Box
                    key={service.productName}
                    onClick={() => {
                      setSelectedService(service.productName);
                      setProductSelectionWarning('');
                      setFormData(prev => ({
                        ...prev,
                        shipmentType: {
                          name: service.productName,
                          price: priceInfo ? `${priceInfo.priceCurrency} ${priceInfo.price}` : '',
                        },
                      }));
                    }}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 1,
                      backgroundColor: '#FFF',
                      boxShadow: selected ? '0 2px 10px rgba(0,0,0,0.15)' : '0 1px 4px rgba(0,0,0,0.08)',
                      mx: selected ? 0 : 3,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: selected ? '1px solid #E5E7EB' : 'none' }}>
                      <Typography sx={{ fontWeight: 500 }}>{service.productName}</Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontWeight: 500 }}>
                          {priceInfo ? `${priceInfo.priceCurrency} ${priceInfo.price}` : ''}
                        </Typography>
                        {tryPrice && (
                          <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                            {formatTry(tryPrice.price)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {selected && delivery && (
                      <Box sx={{ p: 2 }}>
                        <Typography sx={{ lineHeight: 1.6 }}>
                          {new Date(delivery).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })},
                          <br />
                          {new Date(delivery).toLocaleDateString('tr-TR', { weekday: 'long' })},{' '}
                          <span style={{ color: '#DA3831' }}>{getDeliveryTimeText(delivery)}</span>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button sx={{ width: "100%", maxWidth: 235, height: 48, fontWeight: 600 }} variant="outlined" color="error" onClick={() => setShowProductSelection(false)}>
                  Geri
                </Button>
                <Button
                  sx={{ width: "100%", maxWidth: 235, height: 48, fontWeight: 600, backgroundColor: '#DA3831' }}
                  variant="contained"
                  color="error"
                  onClick={() => {
                    if (!selectedService) {
                      setProductSelectionWarning('Ürün seçilmedi');
                      return;
                    }
                    setShowProductSelection(false);
                    setShowRateSummary(true);
                  }}
                >
                  Fiyat Sorgula
                </Button>
              </Box>
            </Stack>
          );
        }
        return (
          <Stack spacing={2}>
            <Typography sx={{ mt: 1 }}>Ne gönderiyorsunuz?</Typography>
            <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Icon icon="mdi:file-document-outline" width={24} height={24} color={formData.docOrPackage === 'Döküman' ? 'white' : '#DA3831'} />}
                  onClick={() => setFormData((prev) => ({ ...prev, docOrPackage: 'Döküman' }))}
                  sx={{
                    height: '44px',
                    backgroundColor: formData.docOrPackage === 'Döküman' ? '#DA3831' : 'white',
                    color: formData.docOrPackage === 'Döküman' ? 'white' : '#DA3831',
                    borderColor: '#DA3831',
                    fontWeight: formData.docOrPackage === 'Döküman' ? 'bold' : 'normal',
                    '&:hover': {
                      backgroundColor: formData.docOrPackage === 'Döküman' ? '#b71c1c' : '#f5f5f5',
                    },
                  }}
                >
                  Döküman
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Icon icon="mdi:package-variant-closed" width={24} height={24} color={formData.docOrPackage === 'Paket' ? 'white' : '#DA3831'} />}
                  onClick={() => setFormData((prev) => ({ ...prev, docOrPackage: 'Paket' }))}
                  sx={{
                    height: '44px',
                    backgroundColor: formData.docOrPackage === 'Paket' ? '#DA3831' : 'white',
                    color: formData.docOrPackage === 'Paket' ? 'white' : '#DA3831',
                    borderColor: '#DA3831',
                    fontWeight: formData.docOrPackage === 'Paket' ? 'bold' : 'normal',
                    '&:hover': {
                      backgroundColor: formData.docOrPackage === 'Paket' ? '#b71c1c' : '#f5f5f5',
                    },
                  }}
                >
                  Paket
                </Button>
            </Stack>
            <Typography sx={{ mt: 2, fontSize: 14 }}>
              {formData.docOrPackage === 'Paket'
                ? 'Müşteriye, gönderisini sigorta yaptırmak isteyip istemediğini sorun (Ek ücretler olacaktır)'
                : 'Müşteriye, gönderisine ekstra koruma isteyip istemediğini sorun (Ek ücretler olacaktır)'}
            </Typography>
            <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Icon icon="mdi:check-circle-outline" width={24} height={24} color={formData.extraProtection === 'Evet' ? 'white' : '#DA3831'} />}
                  onClick={() => handleExtraProtectionChange('Evet')}
                  sx={{
                    height: '44px',
                    backgroundColor: formData.extraProtection === 'Evet' ? '#DA3831' : 'white',
                    color: formData.extraProtection === 'Evet' ? 'white' : '#DA3831',
                    borderColor: '#DA3831',
                    fontWeight: formData.extraProtection === 'Evet' ? 'bold' : 'normal',
                    '&:hover': {
                      backgroundColor: formData.extraProtection === 'Evet' ? '#b71c1c' : '#f5f5f5',
                    },
                  }}
                >
                  Evet
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Icon icon="mdi:close-circle-outline" width={24} height={24} color={formData.extraProtection === 'Hayır' ? 'white' : '#DA3831'} />}
                  onClick={() => handleExtraProtectionChange('Hayır')}
                  sx={{
                    height: '44px',
                    backgroundColor: formData.extraProtection === 'Hayır' ? '#DA3831' : 'white',
                    color: formData.extraProtection === 'Hayır' ? 'white' : '#DA3831',
                    borderColor: '#DA3831',
                    fontWeight: formData.extraProtection === 'Hayır' ? 'bold' : 'normal',
                    '&:hover': {
                      backgroundColor: formData.extraProtection === 'Hayır' ? '#b71c1c' : '#f5f5f5',
                    },
                  }}
                >
                  Hayır
                </Button>
            </Stack>
            <TextField
              label="Lütfen Paket Türü Seçin *"
              InputLabelProps={{ shrink: true }}
              fullWidth
              select
              SelectProps={{ native: true }}
              value={formData.packageType}
              onChange={e => handlePackageTypeChange(e.target.value)}
            >
              <option value="">Lütfen Paket Türü Seçin</option>
              {DHLPackageTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {getPackageTypeLabel(type)}
                </option>
              ))}
            </TextField>
            {selectedPackageType?.isEnvelope && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Ağırlık"
                  type="number"
                  fullWidth
                  value={formData.weight || ''}
                  onChange={e => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                />
                <TextField
                  label="Birim"
                  fullWidth
                  select
                  SelectProps={{ native: true }}
                  value={formData.weightUnit || 'KG'}
                  onChange={e => setFormData(prev => ({ ...prev, weightUnit: e.target.value }))}
                >
                  <option value="KG">KG</option>
                </TextField>
              </Stack>
            )}
            {formData.docOrPackage === 'Paket' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Gönderi Değeri *" type="number" fullWidth value={formData.quoteShipmentValue} onChange={(e) => handleShipmentValueChange(e.target.value)} />
                <TextField
                  label="Para Birimi"
                  fullWidth
                  select
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                  value={formData.quoteCurrency}
                  onChange={(e) => setFormData(f => ({ ...f, quoteCurrency: e.target.value }))}
                >
                  {/* Şimdilik yalnızca EUR destekleniyor. */}
                  <option value="EUR">EUR</option>
                </TextField>
                <TextField
                  label="Sigortalanan Değer"
                  type="number"
                  fullWidth
                  disabled={formData.extraProtection !== 'Evet'}
                  value={formData.quoteInsuredValue}
                  onChange={(e) => setFormData(f => ({ ...f, quoteInsuredValue: e.target.value }))}
                />
              </Stack>
            )}
            <TextField
              label="Ne zaman gönderiyorsunuz?"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={formData.quoteShipmentDate}
              onChange={(e) => setFormData(f => ({ ...f, quoteShipmentDate: e.target.value }))}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button sx={{ width: "100%", maxWidth: 235, height: 48, fontWeight: 600 }} variant="outlined" color="error" onClick={() => setActiveStep((prev) => prev - 1)}>
                Geri
              </Button>
              <Button sx={{ width: "100%", maxWidth: 235, height: 48, fontWeight: 600, backgroundColor: '#DA3831' }} variant="contained" color="error" disabled={isLoading || !formData.packageType || (formData.docOrPackage === 'Paket' && !(Number(formData.quoteShipmentValue) >= 1))} onClick={handleOnFiyatAl}>
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Ön Fiyat Al'}
              </Button>
            </Box>
          </Stack>
        );
      case 3:
        // Gönderi İçeriği adımı: Paket ise detaylı, Döküman ise sade form
        if (formData.docOrPackage === 'Paket') {
          return (
            <Stack spacing={2}>
              {/* Sabit değer; kullanıcı değiştiremiyor. */}
              <TextField label="Gönderi Referansı" fullWidth disabled value={formData.contentReference} />
              <Tooltip
                open={contentDescTooltipOpen}
                placement="top-start"
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                      Kaçının: tek kelime & 8 ya da daha az karakterli tanımlamalar
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>
                      Yazın: aşağıdaki soruların cevapları:
                    </Typography>
                    <Typography sx={{ fontSize: 14 }}>
                      <b>Ürün nedir?</b> (Örnek: Marka/ Üretici/ Model/ Part Numarası)
                    </Typography>
                    <Typography sx={{ fontSize: 14 }}>
                      <b>Neyden yapılmıştır/üretilmiştir?</b> (örnek: Wood/ Plastic / Aluminium)
                    </Typography>
                    <Typography sx={{ fontSize: 14 }}>
                      <b>Ne için kullanılacak?</b> (örnek: tanım/ kullanım amacı )
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 1.5 }}>
                      Açıklamayı İngilizce yazmanız önerilir; gümrük işlemleri
                      İngilizce açıklamayla daha sorunsuz ilerler.
                    </Typography>
                  </Box>
                }
                componentsProps={{
                  tooltip: { sx: { backgroundColor: '#333', color: '#FFF', maxWidth: 620, borderRadius: 1 } },
                }}
              >
                <TextField
                  label="Gönderi Açıklaması *"
                  placeholder="Aluminium phone case for mobile phone protection"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={formData.contentDescription || ''}
                  onChange={e => setFormData(prev => ({ ...prev, contentDescription: e.target.value }))}
                  onFocus={() => setContentDescTooltipOpen(true)}
                  onBlur={() => setContentDescTooltipOpen(false)}
                  onMouseEnter={() => setContentDescTooltipOpen(true)}
                  onMouseLeave={() => setContentDescTooltipOpen(false)}
                  error={!!errors.contentDescription}
                  helperText={errors.contentDescription}
                />
              </Tooltip>
              <TextField
                label="İhracat Tipi"
                fullWidth
                select
                value={formData.exportReason}
                onChange={e => setFormData(prev => ({ ...prev, exportReason: e.target.value }))}
              >
                {EXPORT_REASONS.map(reason => (
                  <MenuItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField label="Diğer Yorumlar" fullWidth multiline minRows={3} value={formData.otherComments || ''} onChange={e => setFormData(prev => ({ ...prev, otherComments: e.target.value }))} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button sx={{ width: "100%", maxWidth: 235, height: 48, fontWeight: 600 }} variant="outlined" color="error" onClick={() => setActiveStep((prev) => prev - 1)}>
                  Geri
                </Button>
                <Button sx={{ width: "100%", maxWidth: 235, height: 48, fontWeight: 600, backgroundColor: '#DA3831' }} variant="contained" color="error" onClick={() => { if (validateStep(3)) { goToCustomsStep() } }}>
                  Sonraki
                </Button>
              </Box>
            </Stack>
          );
        } else {
          return (
            <Stack spacing={2}>
              {/* DHL'de döküman gönderilerinde yalnızca döküman tipi ve referans
                  soruluyor (bu sırayla); ayrı bir gönderi açıklaması alanı yok. */}
              <TextField
                select
                label="Döküman Tipi *"
                fullWidth
                value={formData.documentType}
                onChange={e => setFormData(prev => ({ ...prev, documentType: e.target.value }))}
                SelectProps={{ native: false }}
                error={!!errors.documentType}
                helperText={errors.documentType}
              >
                {DOCUMENT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
              {/* Sabit değer; kullanıcı değiştiremiyor. */}
              <TextField label="Gönderi Referansı" fullWidth disabled value={formData.contentReference} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button sx={{ width: "100%", maxWidth: 235 }} variant="outlined" color="error" onClick={() => setActiveStep((prev) => prev - 1)}>
                  Önceki
                </Button>
                <Button
                  sx={{ width: "100%", maxWidth: 235, height: 48, fontWeight: 600, backgroundColor: '#DA3831' }}
                  variant="contained"
                  color="error"
                  onClick={goToSummary}
                >
                  Sonraki
                </Button>
              </Box>
            </Stack>
          );
        }
      case 4:
        return (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h5" fontWeight={700}>Öğe {formData.items.length + 1}</Typography>
              <Button
                variant="contained"
                color="error"
                sx={{ backgroundColor: '#DA3831', minWidth: 100, fontWeight: 600 }}
                onClick={() => setItemForm(EMPTY_ITEM)}
              >
                Sil
              </Button>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Paket Türü *"
                fullWidth
                select
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                value={itemForm.packageType}
                onChange={e => setItemForm(f => ({ ...f, packageType: e.target.value }))}
              >
                <option value="">Seçiniz</option>
                {CUSTOMS_PACKAGE_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </TextField>
              <Button variant="contained" color="error" fullWidth sx={{ backgroundColor: '#DA3831', height: 56, fontWeight: 600 }} onClick={() => setShowDescModal(true)}>Tanım Oluştur</Button>
            </Stack>
            <TextField
              label="Tanım *"
              fullWidth
              multiline
              minRows={3}
              value={itemForm.description}
              onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))}
              onFocus={e => {
                if ((itemForm.description || '').length <= 5) {
                  setDescTooltipAnchor(e.currentTarget);
                }
              }}
            />

            <Stack sx={{ display: 'flex', alignItems: 'center', position: 'relative', width: 'fit-content' }}>
              <Popover
                open={Boolean(descTooltipAnchor) && (itemForm.description || '').length <= 5}
                anchorEl={descTooltipAnchor}
                onClose={() => setDescTooltipAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ sx: { p: 2, maxWidth: 596, position: 'relative', pb: 3, overflow: 'visible' } }}
                disableRestoreFocus
                disableEnforceFocus
                disableAutoFocus
              >
                <Typography sx={{ fontSize: 15, fontWeight: 500, mb: 1 }}>Tek kelime ya da daha az karakterli tanımlamalarından Kaçının. Yazın, aşağıdaki soruları cevaplayın:</Typography>
                <Typography sx={{ fontSize: 14, mb: 0.5 }}><span style={{ fontWeight: 'bold' }}>Ürün nedir?</span> (Örnek: Marka/ Üretici/Model/Part Numarası)</Typography>
                <Typography sx={{ fontSize: 14, mb: 0.5 }}><span style={{ fontWeight: 'bold' }}>Neyden yapılmıştır/üretilmiştir?</span> (örnek: Wood/Plastic/Aluminium)</Typography>
                <Typography sx={{ fontSize: 14, mb: 0.5 }}><span style={{ fontWeight: 'bold' }}>Ne için kullanılacak?</span> (örnek: tanım/kullanım amacı)</Typography>
                <Typography onClick={() => setShowDescModal(true)} sx={{ fontSize: 14, mt: 1, cursor: 'pointer' }}><span style={{ fontWeight: 'bold' }}>Tanım Oluştur</span> butonunu kullanarak tanım oluşturabilirsiniz.</Typography>
                <Box sx={{
                  position: 'absolute',
                  left: '0',
                  bottom: -14,
                  width: 0,
                  height: 0,
                  borderLeft: '12px solid transparent',
                  borderRight: '12px solid transparent',
                  borderTop: '16px solid #fff',
                  filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.08))',
                  zIndex: 10
                }} />
              </Popover>
            </Stack>
            <Dialog open={showDescModal} onClose={() => setShowDescModal(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Ürünlerinizi tanımlayın</DialogTitle>
              <DialogContent>
                <Typography sx={{ mb: 2 }}>Ürünlerinizi detaylı olarak açıkladığınızdan emin olun. Doğru tanımlamalar, gönderinin uygunluğunu değerlendirmek için kullanılır ve gönderinizin sorunsuz şekilde hareket etmesine yardım eder.</Typography>
                <Stack spacing={2}>
                  <TextField label="Ürün nedir?" placeholder="Örnek: Shoes or toys" fullWidth value={descModalFields.product} onChange={e => setDescModalFields(f => ({ ...f, product: e.target.value }))} />
                  <TextField label="Ürün ya da Marka" placeholder="Ürünü üreten marka ya da üretici kimdir?" fullWidth value={descModalFields.brand} onChange={e => setDescModalFields(f => ({ ...f, brand: e.target.value }))} />
                  <TextField label="Modeli, stili ya da türü nedir?" placeholder="Örnek: M5797U" fullWidth value={descModalFields.model} onChange={e => setDescModalFields(f => ({ ...f, model: e.target.value }))} />
                  <TextField label="Neyden yapılmıştır?" placeholder="Örnek: Plastic, aluminum, wood" fullWidth value={descModalFields.material} onChange={e => setDescModalFields(f => ({ ...f, material: e.target.value }))} />
                  <TextField label="Nasıl kullanılacak?" placeholder="Örnek: Athletic activity or children's play" fullWidth value={descModalFields.usage} onChange={e => setDescModalFields(f => ({ ...f, usage: e.target.value }))} />
                  <TextField label="Ürün açıklaması" placeholder="Ürün açıklaması" fullWidth value={descModalFields.description} onChange={e => setDescModalFields(f => ({ ...f, description: e.target.value }))} />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button variant="contained" color="error" onClick={() => {
                  const allDesc = [descModalFields.product, descModalFields.brand, descModalFields.model, descModalFields.material, descModalFields.usage, descModalFields.description].filter(Boolean).join(' - ');
                  setItemForm(f => ({ ...f, description: allDesc }));
                  setShowDescModal(false);
                }}>Tamam</Button>
              </DialogActions>
            </Dialog>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Adet *" fullWidth value={itemForm.quantity} onChange={e => setItemForm(f => ({ ...f, quantity: e.target.value }))} />
              <TextField label="Net Ağırlık * (KG)" fullWidth value={itemForm.netWeight} onChange={e => setItemForm(f => ({ ...f, netWeight: e.target.value }))} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label={`Birim Değeri* (${formData.quoteCurrency})`} fullWidth value={itemForm.unitValue} onChange={e => setItemForm(f => ({ ...f, unitValue: e.target.value }))} />
              <CountryAutocomplete
                label="Menşei Ülke (Üretim Ülkesi)"
                showCodeOnly
                value={itemForm.originCountry}
                onChange={code => setItemForm(f => ({ ...f, originCountry: code }))}
              />
            </Stack>
            {/* Eklenen öğeler listesi */}
            {formData.items.length > 0 && (
              <Box mt={3}>
                {formData.items.map((item, idx) => (
                  <Paper key={idx} sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography fontWeight={600}>Öğe {idx + 1}</Typography>
                      <Typography>Paket Türü: {item.packageType}</Typography>
                      <Typography>Tanım: {item.description}</Typography>
                      <Typography>Adet: {item.quantity} | Net Ağırlık: {item.netWeight} kg</Typography>
                      <Typography>Birim Değeri: {item.unitValue} | Menşei Ülke: {getCountryLabel(item.originCountry) || '-'}</Typography>
                    </Box>
                    <Button variant="outlined" color="error" onClick={() => setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }))}>Sil</Button>
                  </Paper>
                ))}
              </Box>
            )}
            {errors.items && <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>{errors.items}</Typography>}
            <Box>
              <Button
                variant="contained"
                color="error"
                sx={{ backgroundColor: '#DA3831', fontWeight: 600, height: 48, minWidth: 200 }}
                onClick={() => {
                  if (!isItemComplete(itemForm)) {
                    setErrors(prev => ({ ...prev, items: 'Öğe eklemek için tüm alanları doldurun.' }));
                    return;
                  }
                  setFormData(prev => ({ ...prev, items: [...prev.items, itemForm] }));
                  setItemForm(EMPTY_ITEM);
                  setErrors(prev => ({ ...prev, items: undefined }));
                }}
              >
                Yeni öğe ekle
              </Button>
            </Box>

            {/* DHL'deki toplam satırları: eklenen öğeler + düzenlenmekte olan öğe */}
            <Box sx={{ borderTop: '3px solid #111', mt: 4, pt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #E5E7EB', py: 1.5 }}>
                <Typography>Toplam Gönderi Değeri</Typography>
                <Typography sx={{ color: '#1E40AF' }}>{customsTotals.value} ({formData.quoteCurrency})</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #E5E7EB', py: 1.5 }}>
                <Typography>Toplam Ağırlık</Typography>
                <Typography sx={{ color: '#1E40AF' }}>{customsTotals.weight} (KG)</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button variant="outlined" color="error" sx={{ width: '100%', maxWidth: 235, height: 48, fontWeight: 600 }} onClick={() => setActiveStep((prev) => prev - 1)}>
                Geri
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={goToSummary}
                sx={{ width: '100%', maxWidth: 235, height: 48, fontWeight: 600, backgroundColor: '#DA3831' }}
              >
                Sonraki
              </Button>
            </Box>
          </Stack>
        );
      default:
        return null;
    }
  };

  // Sağ panelde seçili gönderi tipinin tüm detaylarını bul
  const selectedServiceObj = apiProducts.find(s => s.productName === selectedService) || (apiProducts.length > 0 ? apiProducts[0] : null);


  // Gönderi Özeti Section
  // Hata modalı. Özet ve waybill ekranları erken return ettiği için tek bir
  // yerde tutulup her dalda render ediliyor.
  const globalModals = (
    <>
      <Dialog open={showErrorModal} onClose={() => setShowErrorModal(false)}>
        <DialogTitle>Bir Sorun Oluştu</DialogTitle>
        <DialogContent>
          <Typography>{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorModal(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

    </>
  );

  if (showBildirSection) {
    return (
      <Box sx={{ backgroundColor: "#FFF", width: '100%', height: '100%', paddingInline: '5%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', pt: 6 }}>
        <Stack sx={{ width: '100%', maxWidth: 680, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, borderBottom: '1px solid #ECE8E8', width: '100%', pb: 1, }}>
            <Typography variant="p" sx={{ pb: 1, color: '#515151', fontSize: "16px", fontWeight: 600 }}>Gönderi Özeti</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
            <Box>
              <Typography fontWeight={500} sx={{ color: '#515151', fontSize: 15 }}>Waybill (Konşimento) numarası</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 2 }}>{shipmentResponse?.shipmentTrackingNumber || 'Yükleniyor...'}</Typography>
              <Typography fontWeight={500} sx={{ color: '#515151', fontSize: 15 }}>Referans Numarası</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 2 }}>{shipmentResponse?.packages?.[0]?.trackingNumber || 'Yükleniyor...'}</Typography>
            </Box>
            <Box>
              <Typography fontWeight={500} sx={{ color: '#515151', fontSize: 15 }}>Oluşturma Tarihi</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 2 }}>
                {formatDate(creationDate)}
              </Typography>
              <Typography fontWeight={500} sx={{ color: '#515151', fontSize: 15 }}>Genel Toplam</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#DA3831' }}>{selectedServiceObj?.totalPrice?.[0] ? `${selectedServiceObj.totalPrice[0].priceCurrency} ${selectedServiceObj.totalPrice[0].price}` : '-'}</Typography>
              {selectedServiceObj?.totalPrice?.find(p => p.priceCurrency === 'TRY') && (
                <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
                  {formatTry(selectedServiceObj.totalPrice.find(p => p.priceCurrency === 'TRY').price)}
                </Typography>
              )}
            </Box>
          </Box>
          {/* PLT durumu servisten gelir; bilgi gelmediyse hiçbir şey gösterilmez. */}
          {pltInfo?.pltAvailable && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#2E7D32' }}>
                PLT (kağıtsız ticaret) bölgesidir, kağıt fatura gerekmez
              </Typography>
            </Box>
          )}
          {pltInfo && !pltInfo.pltAvailable && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: "space-between", mb: 2 }}>
              <Box sx={{ mr: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>PLT bölgesi değildir</Typography>
                {getPltReasonText(pltInfo) && (
                  <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{getPltReasonText(pltInfo)}</Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={formData.isPerformaNeed}
                  onChange={e => setFormData(prev => ({ ...prev, isPerformaNeed: e.target.checked }))}
                  style={{ accentColor: '#DA3831', marginRight: 6 }}
                />
                <Typography sx={{ fontSize: 14, mr: 1 }}>Proforma faturayı eklediniz mi?</Typography>
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 6, mt: 4, mb: 2 }}>
            <Box sx={{ flex: 1, display: "flex", flexDirection: 'column' }}>
              <Box sx={{ display: "flex" }}>
                <Box>
                  <Typography fontWeight={700} sx={{ mb: 1 }}>ADIM 1</Typography>
                  <Typography sx={{ mb: 2 }}>Gönderiyi Kabul Et'e tıklayın ve alıcıya makbuz gönderin</Typography>
                </Box>
                <img src="/assets/icons/cargo-step-one.png" alt="step one" width={93} height={73} style={{ marginBottom: 8 }} />
              </Box>
              {!isKabulEdildi ? (
                <Button
                  variant="contained"
                  color="error"
                  disabled={isAccepting}
                  sx={{ width: '100%', maxWidth: 235, mt: 'auto', height: 48, backgroundColor: '#DA3831', fontWeight: 600, fontSize: 16 }}
                  onClick={openAcceptConfirm}
                >
                  {isAccepting ? <CircularProgress size={24} color="inherit" /> : 'Gönderiyi Kabul Et'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disabled
                  sx={{
                    width: '100%',
                    maxWidth: 277,
                    mt: 'auto',
                    height: 77,
                    backgroundColor: '#F4F4F4',
                    color: '#B5B5B5',
                    fontWeight: 500,
                    fontSize: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'none',
                    cursor: 'default',
                    textTransform: 'none',
                    p: 0,
                  }}
                >
                  <span style={{ fontSize: 16, color: '#B5B5B5', fontWeight: 500, lineHeight: 1 }}>Gönderi Kabul Edilme Zamanı</span>
                  <span style={{ fontSize: 16, color: '#B5B5B5', fontWeight: 500, lineHeight: 1 }}>
                    {kabulTarihi ? `${kabulTarihi.toLocaleDateString('tr-TR')} - ${kabulTarihi.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}` : '-'}
                  </span>
                </Button>
              )}
            </Box>
            <Box sx={{ flex: 1, display: "flex", flexDirection: 'column' }}>
              <Box sx={{ display: "flex" }}>
                <Box>
                  <Typography fontWeight={700} sx={{ mb: 1 }}>ADIM 2</Typography>
                  <Typography sx={{ mb: 2 }}>Gönderi belgelerini yazdırın ve gönderi ile birlikte DHL kuryesine teslim edin</Typography>
                </Box>
                <img src="/assets/icons/cargo-step-one-v2.png" alt="step one" width={93} height={73} style={{ marginBottom: 8 }} />
              </Box>
              <Button onClick={handlePrintDocuments} variant="outlined" color="error" sx={{ width: '100%', maxWidth: "235", mt: 3, height: 48, color: '#DA3831' }}>Gerekli Belgeleri Yazdır</Button>
            </Box>
          </Box>
          <Stack sx={{ display: 'flex', flexDirection: "column", gap: 1 }}>
            {downloadLinks.map(link => (
              <Box
                key={link.typeCode}
                sx={{
                  width: "fit-content",
                  marginLeft: "auto",
                  color: '#515151',
                  borderBottom: "1px solid #515151",
                  fontSize: "14px",
                  textAlign: "right",
                  alignItems: "flex-end",
                  cursor: isKabulEdildi ? 'pointer' : 'default',
                  pointerEvents: isKabulEdildi ? 'auto' : 'none',
                  opacity: isKabulEdildi ? 1 : 0.5,
                }}
                onClick={isKabulEdildi ? () => downloadDocument(link) : undefined}
              >{link.label}</Box>
            ))}
            <Box
              sx={{
                width: "fit-content",
                marginLeft: "auto",
                color: '#515151',
                borderBottom: "1px solid #515151",
                fontSize: "14px",
                textAlign: "right",
                alignItems: "flex-end",
                cursor: isKabulEdildi ? 'pointer' : 'default',
                pointerEvents: isKabulEdildi ? 'auto' : 'none',
                opacity: isKabulEdildi ? 1 : 0.5,
              }}
              onClick={isKabulEdildi ? () => setShowDetailsModal(true) : undefined}
            >Gönderi Detaylarını Görüntüle</Box>
          </Stack>
          <Stack direction="row" spacing={3} justifyContent="center" mt={5}>
            <Button variant="contained" color="error" sx={{ width: "100%", height: "48px", maxWidth: 345, fontWeight: 600, fontSize: 16, backgroundColor: '#DA3831', marginRight: "auto" }} onClick={handleResetAll}>Başka Bir Gönderi Oluştur</Button>
          </Stack>
        </Stack>
        {/* Gönderi kabulü öncesi personel onayı */}
        <Dialog open={showAcceptConfirm} onClose={() => !isAccepting && setShowAcceptConfirm(false)} maxWidth={false} PaperProps={{ sx: { width: '100%', maxWidth: 560, borderRadius: 2 } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Gönderiyi Kabul Et</Typography>
            <IconButton disabled={isAccepting} onClick={() => setShowAcceptConfirm(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 4, px: 4 }}>
            <Typography sx={{ color: '#515151', fontSize: 15, mb: 3 }}>
              Onayladığınızda gönderi ödendi olarak kaydedilir ve kurye çağrılır.
            </Typography>
            <Stack spacing={2} sx={{ mb: 4 }}>
              <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={acceptChecks.paymentChecked}
                  onChange={e => setAcceptChecks(prev => ({ ...prev, paymentChecked: e.target.checked }))}
                  style={{ accentColor: '#DA3831', width: 18, height: 18 }}
                />
                <Typography sx={{ fontSize: 16 }}>Müşterinin DHL'e yaptığı ödemeyi kontrol ettim</Typography>
              </Box>
              <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={acceptChecks.labelAttached}
                  onChange={e => setAcceptChecks(prev => ({ ...prev, labelAttached: e.target.checked }))}
                  style={{ accentColor: '#DA3831', width: 18, height: 18 }}
                />
                <Typography sx={{ fontSize: 16 }}>Barkodu gönderiye yapıştırdım</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" color="error" disabled={isAccepting} sx={{ flex: 1, height: 48, fontWeight: 600 }} onClick={() => setShowAcceptConfirm(false)}>
                Vazgeç
              </Button>
              <Button
                variant="contained"
                color="error"
                disableElevation
                disabled={isAccepting || !acceptChecks.paymentChecked || !acceptChecks.labelAttached}
                sx={{ flex: 1, height: 48, fontWeight: 600, backgroundColor: '#DA3831' }}
                onClick={handleAcceptShipment}
              >
                {isAccepting ? <CircularProgress size={24} color="inherit" /> : 'Evet, Kabul Et'}
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>
        {/* Gönderi Kabul İşlemi Sonrası Modal */}
        <Dialog open={showKabulModal} onClose={() => setShowKabulModal(false)} maxWidth={false} PaperProps={{ sx: { width: '100%', maxWidth: 480, borderRadius: 2 } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
            <IconButton sx={{ marginLeft: 'auto' }} onClick={() => setShowKabulModal(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 4, px: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <Typography sx={{ color: '#222', fontSize: 20, fontWeight: 700, mb: 1 }}>
              Gönderi kabul edildi
            </Typography>
            <Typography sx={{ color: '#515151', fontSize: 18, mb: 4 }}>
              Kurye çağrıldı.
            </Typography>
            <Button variant="contained" color="error" sx={{ backgroundColor: '#DA3831', width: 200, height: 48, fontSize: 18, fontWeight: 600 }} onClick={() => setShowKabulModal(false)}>Kapat</Button>
          </DialogContent>
        </Dialog>
        {/* Gönderi Detaylarını Görüntüle Modal - Custom Accordion */}
        <Dialog open={showDetailsModal} onClose={() => setShowDetailsModal(false)} maxWidth={false} PaperProps={{ sx: { width: '100%', maxWidth: 700, borderRadius: 2 } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
            <IconButton sx={{ marginLeft: 'auto' }} onClick={() => setShowDetailsModal(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 4, px: 2 }}>
            <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 2, background: '#fff' }}>
              {/* Kimden */}
              <Box sx={{ borderBottom: '1px solid #E5E7EB', pb: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', p: 2 }} onClick={() => setOpenAccordion(openAccordion === 'from' ? null : 'from')}>
                  <Typography sx={{ fontWeight: 700, fontSize: 18, color: openAccordion === 'from' ? '#DA3831' : '#1F2937', mr: 1 }}>
                    {openAccordion === 'from' ? '–' : '+'}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 18, color: openAccordion === 'from' ? '#DA3831' : '#1F2937' }}>Kimden</Typography>
                </Box>
                {openAccordion === 'from' && (
                  <Box sx={{ mt: 2, ml: 4 }}>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formData.sender.name}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formData.sender.company}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formData.sender.address1}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formData.sender.city}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{getCountryLabel(formData.sender.country)}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formData.sender.postalCode}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formData.sender.email}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formatPhoneDisplay(formData.sender.phone, formData.sender.phoneCode)}</span></Typography>
                  </Box>
                )}
              </Box>
              {/* Kime */}
              <Box sx={{ borderBottom: '1px solid #E5E7EB', pb: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', p: 2 }} onClick={() => setOpenAccordion(openAccordion === 'to' ? null : 'to')}>
                  <Typography sx={{ fontWeight: 700, fontSize: 18, color: openAccordion === 'to' ? '#DA3831' : '#1F2937', mr: 1 }}>
                    {openAccordion === 'to' ? '–' : '+'}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 18, color: openAccordion === 'to' ? '#DA3831' : '#1F2937' }}>Kime</Typography>
                </Box>
                {openAccordion === 'to' && (
                  <Box sx={{ mt: 2, ml: 4 }}>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}> <span style={{ color: '#111' }}>{formData.recipient.name}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formData.recipient.company}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formData.recipient.address1}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formData.recipient.city}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{getCountryLabel(formData.recipient.country)}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formData.recipient.postalCode}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formData.recipient.email}</span></Typography>
                    <Typography sx={{ color: '#6B7280', fontWeight: 500 }}><span style={{ color: '#111' }}>{formatPhoneDisplay(formData.recipient.phone, formData.recipient.phoneCode)}</span></Typography>
                  </Box>
                )}
              </Box>
              {/* Gönderi Detayları */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', p: 2 }} onClick={() => setOpenAccordion(openAccordion === 'details' ? null : 'details')}>
                  <Typography sx={{ fontWeight: 700, fontSize: 18, color: openAccordion === 'details' ? '#DA3831' : '#1F2937', mr: 1 }}>
                    {openAccordion === 'details' ? '–' : '+'}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 18, color: openAccordion === 'details' ? '#DA3831' : '#1F2937' }}>Gönderi Detayları</Typography>
                </Box>
                {openAccordion === 'details' && (
                  <Box sx={{ mt: 2, ml: 4 }}>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography sx={{ color: '#6B7280', fontWeight: 500, minWidth: 140 }}>Gönderi Türü</Typography>
                      <Typography sx={{ color: '#111', fontWeight: 500 }}>{formData.docOrPackage}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography sx={{ color: '#6B7280', fontWeight: 500, minWidth: 140 }}>Ürün</Typography>
                      <Typography sx={{ color: '#111', fontWeight: 500 }}>{selectedServiceObj?.productName || '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography sx={{ color: '#6B7280', fontWeight: 500, minWidth: 140 }}>Tarih</Typography>
                      <Typography sx={{ color: '#111', fontWeight: 500 }}>
                        {formatDate(creationDate)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography sx={{ color: '#6B7280', fontWeight: 500, minWidth: 140 }}>Son Kayıt Saati</Typography>
                      <Typography sx={{ color: '#111', fontWeight: 500 }}>{selectedServiceObj?.pickupCapabilities?.localCutoffDateAndTime || '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography sx={{ color: '#6B7280', fontWeight: 500, minWidth: 140 }}>Ürün Türü</Typography>
                      <Typography sx={{ color: '#111', fontWeight: 500 }}>
                        {(formData.docOrPackage === 'Döküman' ? formData.documentType : formData.contentDescription) || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography sx={{ color: '#6B7280', fontWeight: 500, minWidth: 140 }}>Toplam Tutar</Typography>
                      <Box>
                        <Typography sx={{ color: '#DA3831', fontWeight: 600, textDecoration: 'underline' }}>{selectedServiceObj?.totalPrice?.[0] ? `${selectedServiceObj.totalPrice[0].priceCurrency} ${selectedServiceObj.totalPrice[0].price}` : '-'}</Typography>
                        {selectedServiceObj?.totalPrice?.find(p => p.priceCurrency === 'TRY') && (
                          <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
                            {formatTry(selectedServiceObj.totalPrice.find(p => p.priceCurrency === 'TRY').price)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
        {/* Gönderi Kabul Edilmedi Modal */}
        <Dialog open={showNotAcceptedModal} onClose={() => setShowNotAcceptedModal(false)} maxWidth={false} PaperProps={{ sx: { width: '100%', maxWidth: 600, borderRadius: 2 } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', pb: 0 }}>
            <IconButton sx={{ marginLeft: 'auto' }} onClick={() => setShowNotAcceptedModal(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 4, px: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
            <img src="/assets/icons/cargo-step-one-v2.png" alt="step one" style={{ width: 180, height: 120, marginBottom: 24, alignSelf: 'center' }} />
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#222', mb: 2, mt: 1 }}>GÖNDERİ KABUL EDİLMEDİ</Typography>
            <Typography sx={{ color: '#515151', fontSize: 16, mb: 4, textAlign: 'left' }}>
              Lütfen belgeleri yazdırmadan önce gönderiyi kabul edin.
            </Typography>
            <Button variant="contained" color="error" sx={{ backgroundColor: '#DA3831', width: 200, height: 48, fontSize: 18, fontWeight: 600 }} onClick={() => setShowNotAcceptedModal(false)}>Tamam</Button>
          </DialogContent>
        </Dialog>
        {globalModals}
      </Box>
    );
  }

  if (showSummary) {
    return (
      <Box sx={{ backgroundColor: "#FFF", width: '100%', height: '100%', paddingInline: '5%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', pt: 6 }}>

        <Stack sx={{ width: '100%', maxWidth: 680, mb: 4, borderRadius: 3 }}>
          <Typography variant="p" sx={{ pb: 1, mb: 6, color: '#515151', fontSize: "16px", fontWeight: 600, borderBottom: '1px solid #ECE8E8' }}>Gönderi Özeti</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 6, mb: 4 }}>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={500} sx={{ color: '#515151', fontSize: 15 }}>Gönderi Türü</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 2 }}>{formData.docOrPackage === 'Paket' ? 'PARSEL' : 'DÖKÜMAN'}</Typography>
              <Typography fontWeight={500} sx={{ color: '#515151', fontSize: 15 }}>Tarih</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 2 }}>{formatDate(formData.quoteShipmentDate)}</Typography>
              <Typography fontWeight={500} sx={{ color: '#515151', fontSize: 15 }}>Ürün türü</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                {(formData.docOrPackage === 'Döküman' ? formData.documentType : formData.contentDescription) || '-'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={500} sx={{ color: '#515151', fontSize: 15 }}>Ürün</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 2 }}>{selectedServiceObj?.productName || '-'}</Typography>
              <Typography fontWeight={500} sx={{ color: '#515151', fontSize: 15 }}>Son Kayıt Saati</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 2 }}>{selectedServiceObj?.pickupCapabilities?.localCutoffDateAndTime || '-'}</Typography>
              <Typography fontWeight={500} sx={{ color: '#515151', fontSize: 15 }}>Toplam Tutar</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 16 }}>{selectedServiceObj && selectedServiceObj.totalPrice ? `${selectedServiceObj.totalPrice[0].priceCurrency} ${selectedServiceObj.totalPrice[0].price}` : '-'}</Typography>
              {selectedServiceObj?.totalPrice?.find(p => p.priceCurrency === 'TRY') && (
                <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
                  {formatTry(selectedServiceObj.totalPrice.find(p => p.priceCurrency === 'TRY').price)}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ borderTop: '3px solid #111', pt: 4, display: 'flex', justifyContent: 'space-between', gap: 6 }}>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={500} sx={{ color: '#515151', fontSize: 15 }}>Alıcı</Typography>
              <Typography>{formData.recipient.name}</Typography>
              <Typography>{formData.recipient.company}</Typography>
              <Typography>{formData.recipient.address1}</Typography>
              <Typography>{formData.recipient.address2}</Typography>
              <Typography sx={{ mt: 2 }}>{(formData.recipient.city || '').toLocaleUpperCase('tr-TR')}</Typography>
              <Typography>{getCountryNameUpper(formData.recipient.country)}</Typography>
              <Typography>{formData.recipient.postalCode}</Typography>
              <Typography>{formData.recipient.email}</Typography>
              <Typography>{formatPhoneDisplay(formData.recipient.phone, formData.recipient.phoneCode)}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={500} sx={{ color: '#515151', fontSize: 15 }}>Gönderici</Typography>
              <Typography>{formData.sender.name}</Typography>
              <Typography>{formData.sender.company}</Typography>
              <Typography>{formData.sender.address1}</Typography>
              <Typography>{formData.sender.address2}</Typography>
              <Typography sx={{ mt: 2 }}>{(formData.sender.city || '').toLocaleUpperCase('tr-TR')}</Typography>
              <Typography>{getCountryNameUpper(formData.sender.country)}</Typography>
              <Typography>{formData.sender.postalCode}</Typography>
              <Typography>{formData.sender.email}</Typography>
              <Typography>{formatPhoneDisplay(formData.sender.phone, formData.sender.phoneCode)}</Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={3} justifyContent="center" mt={5}>
            <Button variant="outlined" color="error" sx={{ width: "100%", height: "48px", maxWidth: 235, fontWeight: 600, fontSize: 16 }} onClick={handleBackFromSummary}>Geri</Button>
            {/* Ödeme DHL'in kendi sitesinden yapılıyor; burada doğrudan gönderi oluşturulur. */}
            <Button variant="contained" color="error" disabled={isLoading} sx={{ backgroundColor: "#DA3831", width: "100%", height: "48px", maxWidth: 235, fontWeight: 600, fontSize: 16 }} onClick={handleCreateDHLCargo}>
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Bildir'}
            </Button>
          </Stack>
        </Stack>
        {globalModals}
      </Box>
    );
  }

  // Ücret Detayları 
  return (
    <Box sx={{ width: '100%', height: '100%', paddingInline: "5%", display: 'flex', justifyContent: 'space-between', gap: 5 }}>
      <Box sx={{ width: '100%' }}>
        <Typography variant="p" marginBottom={5} display={"block"}>DHL Gönderisi Oluşturma</Typography>
        {!fiyatSorgulandi && (
          <Box sx={{ width: '100%', maxWidth: '680px', display: 'flex', gap: 2, mb: 0.5, mt: 2 }}>
            <Box sx={{ width: '50%', borderTop: '5px solid #DA3831', transition: 'border-color 0.2s', fontSize: '13px', paddingTop: 1 }}>Fiyat ve Tarih Sorgulama</Box>
            <Box sx={{ width: '50%', borderTop: '5px solid #ECEFF1', transition: 'border-color 0.2s', fontSize: '13px', paddingTop: 1 }}>Gönderi Oluştur</Box>
          </Box>
        )}
        {!fiyatSorgulandi && activeTab === 0 && (
          <Box sx={{ mt: 3, maxWidth: '680px', width: '100%' }}>
            <Typography variant="h6">Fiyat ve Tarih Sorgulama</Typography>
            <Stack spacing={2} mt={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <CountryAutocomplete
                  value={formData.quoteReceiverCountry}
                  onChange={(code) => setFormData(f => ({
                    ...f,
                    quoteReceiverCountry: code,
                    quoteReceiverPostalCode: '',
                    quoteReceiverCity: '',
                  }))}
                />
                <CityAutocomplete
                  countryCode={formData.quoteReceiverCountry}
                  addressType="delivery"
                  value={formData.quoteReceiverCity}
                  onChange={(city) => setFormData(f => ({ ...f, quoteReceiverCity: city }))}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <PostalCodeAutocomplete
                  disabled={isNoPostalCodeCountry(formData.quoteReceiverCountry)}
                  helperText={isNoPostalCodeCountry(formData.quoteReceiverCountry) ? 'Bu ülkede posta kodu kullanılmıyor.' : undefined}
                  countryCode={formData.quoteReceiverCountry}
                  value={formData.quoteReceiverPostalCode}
                  onChange={({ postalCode, city }) => setFormData(f => ({
                    ...f,
                    quoteReceiverPostalCode: postalCode,
                    quoteReceiverCity: city || f.quoteReceiverCity,
                  }))}
                />
                <TextField
                  label="Ne Zaman Gönderiyorsunuz?"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={formData.quoteShipmentDate}
                  onChange={(e) => setFormData(f => ({ ...f, quoteShipmentDate: e.target.value }))}
                />
              </Stack>
              <Typography sx={{ mt: 2 }}>Ne gönderiyorsunuz?</Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Icon icon="mdi:file-document-outline" width={24} height={24} color={formData.docOrPackage === 'Döküman' ? 'white' : '#DA3831'} />}
                  onClick={() => setFormData((prev) => ({ ...prev, docOrPackage: 'Döküman' }))}
                  sx={{
                    height: '44px',
                    backgroundColor: formData.docOrPackage === 'Döküman' ? '#DA3831' : 'white',
                    color: formData.docOrPackage === 'Döküman' ? 'white' : '#DA3831',
                    borderColor: '#DA3831',
                    fontWeight: formData.docOrPackage === 'Döküman' ? 'bold' : 'normal',
                    '&:hover': {
                      backgroundColor: formData.docOrPackage === 'Döküman' ? '#b71c1c' : '#f5f5f5',
                    },
                  }}
                >
                  Döküman
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Icon icon="mdi:package-variant-closed" width={24} height={24} color={formData.docOrPackage === 'Paket' ? 'white' : '#DA3831'} />}
                  onClick={() => setFormData((prev) => ({ ...prev, docOrPackage: 'Paket' }))}
                  sx={{
                    height: '44px',
                    backgroundColor: formData.docOrPackage === 'Paket' ? '#DA3831' : 'white',
                    color: formData.docOrPackage === 'Paket' ? 'white' : '#DA3831',
                    borderColor: '#DA3831',
                    fontWeight: formData.docOrPackage === 'Paket' ? 'bold' : 'normal',
                    '&:hover': {
                      backgroundColor: formData.docOrPackage === 'Paket' ? '#b71c1c' : '#f5f5f5',
                    },
                  }}
                >
                  Paket
                </Button>
              </Stack>
              <Typography sx={{ mt: 2, fontSize: 14 }}>
                {formData.docOrPackage === 'Paket'
                  ? 'Müşteriye, gönderisini sigorta yaptırmak isteyip istemediğini sorun (Ek ücretler olacaktır)'
                  : 'Müşteriye, gönderisine ekstra koruma isteyip istemediğini sorun (Ek ücretler olacaktır)'}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Icon icon="mdi:check-circle-outline" width={24} height={24} color={formData.extraProtection === 'Evet' ? 'white' : '#DA3831'} />}
                  onClick={() => handleExtraProtectionChange('Evet')}
                  sx={{
                    height: '44px',
                    backgroundColor: formData.extraProtection === 'Evet' ? '#DA3831' : 'white',
                    color: formData.extraProtection === 'Evet' ? 'white' : '#DA3831',
                    borderColor: '#DA3831',
                    fontWeight: formData.extraProtection === 'Evet' ? 'bold' : 'normal',
                    '&:hover': {
                      backgroundColor: formData.extraProtection === 'Evet' ? '#b71c1c' : '#f5f5f5',
                    },
                  }}
                >
                  Evet
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Icon icon="mdi:close-circle-outline" width={24} height={24} color={formData.extraProtection === 'Hayır' ? 'white' : '#DA3831'} />}
                  onClick={() => handleExtraProtectionChange('Hayır')}
                  sx={{
                    height: '44px',
                    backgroundColor: formData.extraProtection === 'Hayır' ? '#DA3831' : 'white',
                    color: formData.extraProtection === 'Hayır' ? 'white' : '#DA3831',
                    borderColor: '#DA3831',
                    fontWeight: formData.extraProtection === 'Hayır' ? 'bold' : 'normal',
                    '&:hover': {
                      backgroundColor: formData.extraProtection === 'Hayır' ? '#b71c1c' : '#f5f5f5',
                    },
                  }}
                >
                  Hayır
                </Button>
              </Stack>
              <TextField
                label="Lütfen Paket Türü Seçin *"
                InputLabelProps={{ shrink: true }}
                fullWidth
                select
                SelectProps={{ native: true }}
                value={formData.packageType}
                onChange={e => handlePackageTypeChange(e.target.value)}
              >
                <option value="">Lütfen Paket Türü Seçin</option>
                {DHLPackageTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {getPackageTypeLabel(type)}
                  </option>
                ))}
              </TextField>
              {/* Ağırlık yalnızca zarfta sorulur; kutuların ağırlığı sabittir. */}
              {selectedPackageType?.isEnvelope && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Ağırlık"
                    type="number"
                    fullWidth
                    value={formData.weight || ''}
                    onChange={e => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                  />
                  <TextField
                    label="Birim"
                    fullWidth
                    select
                    SelectProps={{ native: true }}
                    value={formData.weightUnit || 'KG'}
                    onChange={e => setFormData(prev => ({ ...prev, weightUnit: e.target.value }))}
                  >
                    <option value="KG">KG</option>
                  </TextField>
                </Stack>
              )}
              {/* Değer alanları DHL'de yalnızca Paket seçiliyken görünür. */}
              {formData.docOrPackage === 'Paket' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="Gönderi Değeri *" type="number" fullWidth value={formData.quoteShipmentValue} onChange={(e) => handleShipmentValueChange(e.target.value)} />
                  <TextField
                  label="Para Birimi"
                  fullWidth
                  select
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                  value={formData.quoteCurrency}
                  onChange={(e) => setFormData(f => ({ ...f, quoteCurrency: e.target.value }))}
                >
                  {/* Şimdilik yalnızca EUR destekleniyor. */}
                  <option value="EUR">EUR</option>
                </TextField>
                  <TextField
                    label="Sigortalanan Değer"
                    type="number"
                    fullWidth
                    disabled={formData.extraProtection !== 'Evet'}
                    value={formData.quoteInsuredValue}
                    onChange={(e) => setFormData(f => ({ ...f, quoteInsuredValue: e.target.value }))}
                  />
                </Stack>
              )}
              <Button sx={{
                width: "100%",
                maxWidth: "235px",
                alignSelf: "flex-end",
                backgroundColor: "#DA3831"
              }} variant="contained" color="error" size="large" disabled={isLoading || !formData.packageType || (formData.docOrPackage === 'Paket' && !(Number(formData.quoteShipmentValue) >= 1))} onClick={handleFiyatSorgula}>
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Fiyat Sorgula'}
              </Button>
            </Stack>
          </Box>
        )}
        {/* Ücret detayları — DHL'de fiyat sorgusu ile gönderi formu arasındaki ekran */}
        {fiyatSorgulandi && showRateDetails && (
          <Box sx={{ mt: 3, maxWidth: '680px', width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Ücret detayları</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, border: '1px solid #E5E7EB', borderRadius: 1, p: 1.5, mb: 3 }}>
              <TextField
                label="Promosyon Kodu"
                placeholder="Promosyon Kodu"
                InputLabelProps={{ shrink: true }}
                variant="standard"
                fullWidth
                InputProps={{ disableUnderline: true }}
                value={promotionCode}
                onChange={(e) => setPromotionCode(e.target.value)}
              />
              <Button
                variant="contained"
                color="error"
                disabled
                startIcon={<Icon icon="mdi:gift-outline" width={20} height={20} />}
                sx={{ backgroundColor: '#DA3831', minWidth: 140, height: 44, fontWeight: 600 }}
              >
                Uygula
              </Button>
            </Box>

            {apiProducts.map((product) => {
              const isSelected = product.productName === selectedService;
              const price = product.totalPrice?.[0];
              // DHL fiyatı iki para biriminde döner; TL karşılığı hazır geliyor.
              const tryPrice = product.totalPrice?.find(p => p.priceCurrency === 'TRY');
              const delivery = product.deliveryCapabilities?.estimatedDeliveryDateAndTime;
              return (
                <Box
                  key={product.productName}
                  onClick={() => setSelectedService(product.productName)}
                  sx={{
                    cursor: 'pointer',
                    mb: 2,
                    borderRadius: 1,
                    backgroundColor: '#FFF',
                    boxShadow: isSelected ? '0 2px 10px rgba(0,0,0,0.15)' : '0 1px 4px rgba(0,0,0,0.08)',
                    mx: isSelected ? 0 : 3,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: isSelected ? '1px solid #E5E7EB' : 'none' }}>
                    <Typography sx={{ fontWeight: 500 }}>{product.productName}</Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontWeight: 500 }}>
                        {price?.priceCurrency} {price?.price}
                      </Typography>
                      {tryPrice && (
                        <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                          {formatTry(tryPrice.price)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {isSelected && delivery && (
                    <Box sx={{ p: 2 }}>
                      <Typography sx={{ lineHeight: 1.6 }}>
                        {new Date(delivery).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })},
                        <br />
                        {new Date(delivery).toLocaleDateString('tr-TR', { weekday: 'long' })},{' '}
                        <span style={{ color: '#DA3831' }}>
                          {getDeliveryTimeText(delivery)}
                        </span>
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}

            <Stack direction="row" spacing={3} sx={{ mt: 5 }}>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                startIcon={<Icon icon="mdi:chevron-left" width={20} height={20} />}
                sx={{ height: 48, fontWeight: 600 }}
                onClick={() => {
                  setShowRateDetails(false);
                  setFiyatSorgulandi(false);
                  setActiveTab(0);
                }}
              >
                Geri
              </Button>
              <Button
                variant="contained"
                color="error"
                fullWidth
                endIcon={<Icon icon="mdi:chevron-right" width={20} height={20} />}
                sx={{ backgroundColor: '#DA3831', height: 48, fontWeight: 600 }}
                onClick={() => {
                  // DHL'de sorgudaki ülke/şehir/posta kodu alıcı formuna taşınıyor.
                  setFormData(prev => ({
                    ...prev,
                    recipient: {
                      ...prev.recipient,
                      country: prev.quoteReceiverCountry || prev.recipient.country,
                      city: prev.quoteReceiverCity || prev.recipient.city,
                      postalCode: prev.quoteReceiverPostalCode || prev.recipient.postalCode,
                      phoneCode:
                        getDialCode(prev.quoteReceiverCountry) || prev.recipient.phoneCode,
                    },
                  }));
                  setShowRateDetails(false);
                }}
              >
                Yeni Gönderi Hazırla
              </Button>
            </Stack>
          </Box>
        )}
        {(fiyatSorgulandi || activeTab === 1) && !showRateDetails && (
          <>
            {/* Stepper başlıkları, tab gibi */}
            <Box sx={{ display: 'flex', gap: 3, mt: 4, mb: 3, maxWidth: '680px', width: '100%' }}>
              {steps.map((label, idx) => (
                <Box key={label} sx={{ flex: 1, textAlign: 'center' }}>
                  <Box
                    sx={{
                      borderBottom: '4px solid',
                      borderColor: activeStep >= idx ? '#DA3831' : '#ECEFF1',
                      pb: 0.5,
                      mb: 0.5,
                    }}
                  />
                  <Typography
                    sx={{
                      color: activeStep >= idx ? '#DA3831' : '#374151',
                      fontSize: 13,
                      mt: 0.5,
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 3, maxWidth: '680px', width: '100%' }}>
              {renderStepContent(activeStep)}
            </Box>
          </>
        )}
      </Box>
      {globalModals}

    </Box>
  );
}